/**
 * Chunk storage: CHUNK_SIZE × CHUNK_HEIGHT × CHUNK_SIZE uint8 palette of block IDs.
 *
 * Phase 2.21 worldgen: stream arteries +20% width · multi-artery confluence
 * (no X-cross) · variable bank geometry · BRIDGE sheets at road×stream ·
 * inherits 2.20 pond/sand detours · 2.16 SITE_PAD/T-merge/PATH priority · no caves.
 */

import { BLOCK } from "./blocks";
import { fbm2D, rand2 } from "./noise";

export const CHUNK_SIZE = 16;
/**
 * Vertical column — full rescale Phase 2.5+: compressed land band + tree headroom.
 * Band law: y0 bedrock · y1–30 dirt · sea 10 · surface~15 · max land~30 · H=128 (~98 headroom).
 * Was 256 @ sea 48 (pre-rescale); volume cut ~50% for resident-ring FPS.
 */
export const CHUNK_HEIGHT = 128;
/** Sea / flat baseline height in world Y (block units). */
export const SEA_LEVEL = 10;
/** Soft max landform top (trees/props sit above; clamp foot-gun). */
export const MAX_LAND_HEIGHT = 30;
/** Typical surface-at-start / gentle hill bias target. */
export const SURFACE_TARGET = 15;
/** Max trunk + foliage height (total). Thicker trunks shorten informally below this. */
export const TREE_MAX_TOTAL_HEIGHT = 90;

/**
 * Spawn soft-basin radius (blocks). Near origin, height eases toward SEA_LEVEL
 * so the opening play space is gentler (playtest 1a feedback).
 */
export const SPAWN_FLATTEN_RADIUS = 72;
/**
 * No beach/low sand (surface ≤ sea−1) inside this origin disk (playtest 2.19).
 * Floors landform to SEA_LEVEL so hub/approach stays solid grass, not aqua sand.
 */
export const SAND_ORIGIN_BAN = 500;
/** Soft road keep-out past sand/beach edge (blocks) — arteries path around. */
const SAND_PATH_BUFFER = 18;
/** Soft road keep-out past pond shoreline (blocks) — continuous envelope detour. */
const POND_PATH_BUFFER = 22;
/** Max lateral search when routing arteries around sand/pond obstacles. */
const DETOUR_SEARCH_MAX = 240;
const DETOUR_SEARCH_STEP = 8;

const VOLUME = CHUNK_SIZE * CHUNK_HEIGHT * CHUNK_SIZE;

export type ChunkCoord = { cx: number; cz: number };

export function chunkKey(cx: number, cz: number): string {
  return `${cx},${cz}`;
}

export function worldToChunk(wx: number, wz: number): ChunkCoord {
  return {
    cx: Math.floor(wx / CHUNK_SIZE),
    cz: Math.floor(wz / CHUNK_SIZE),
  };
}

export function localIndex(lx: number, ly: number, lz: number): number {
  return lx + lz * CHUNK_SIZE + ly * CHUNK_SIZE * CHUNK_SIZE;
}

export class Chunk {
  readonly cx: number;
  readonly cz: number;
  /** Column-major-ish: x + z*SIZE + y*SIZE*SIZE */
  readonly blocks: Uint8Array;
  dirty = true;
  meshVersion = 0;

  constructor(cx: number, cz: number, blocks?: Uint8Array) {
    this.cx = cx;
    this.cz = cz;
    this.blocks =
      blocks && blocks.length === VOLUME
        ? blocks
        : new Uint8Array(VOLUME); // air
  }

  get(lx: number, ly: number, lz: number): number {
    if (
      lx < 0 ||
      ly < 0 ||
      lz < 0 ||
      lx >= CHUNK_SIZE ||
      ly >= CHUNK_HEIGHT ||
      lz >= CHUNK_SIZE
    ) {
      return BLOCK.AIR;
    }
    return this.blocks[localIndex(lx, ly, lz)]!;
  }

  set(lx: number, ly: number, lz: number, id: number): void {
    if (
      lx < 0 ||
      ly < 0 ||
      lz < 0 ||
      lx >= CHUNK_SIZE ||
      ly >= CHUNK_HEIGHT ||
      lz >= CHUNK_SIZE
    ) {
      return;
    }
    const i = localIndex(lx, ly, lz);
    if (this.blocks[i] !== id) {
      this.blocks[i] = id;
      this.dirty = true;
    }
  }

  /** World-space block get (only if inside this chunk). */
  getWorld(wx: number, wy: number, wz: number): number | null {
    const ox = this.cx * CHUNK_SIZE;
    const oz = this.cz * CHUNK_SIZE;
    const lx = wx - ox;
    const lz = wz - oz;
    if (lx < 0 || lz < 0 || lx >= CHUNK_SIZE || lz >= CHUNK_SIZE) return null;
    return this.get(lx, wy, lz);
  }
}

export type WorldgenMode = "flat" | "noise";

export interface WorldgenOptions {
  seed: number;
  mode: WorldgenMode;
  /** Flat world top solid Y (inclusive surface). */
  flatHeight?: number;
}

/** Biome ids — Phase 2.1 four looks. */
export const BIOME = {
  GRASSLANDS: 0,
  FOREST: 1,
  ROCK_FIELDS: 2,
  SWAMP: 3,
} as const;

export type BiomeId = (typeof BIOME)[keyof typeof BIOME];

/**
 * Moisture/temperature-ish fields → discrete biome.
 * Large-scale frequencies so each biome reads as a region, not confetti.
 * Spawn basin biases grasslands.
 *
 * Playtest 2.7: biome region size +500% (≈6× coarser than 2.6 frequencies).
 */
export function biomeAt(wx: number, wz: number, seed: number): BiomeId {
  // 2.6 used ~0.0016 / 0.0014; +500% size ⇒ divide freqs by 6
  const moist = fbm2D(wx * 0.000267, wz * 0.000267, seed + 77, 4, 2, 0.55);
  const temp = fbm2D(wx * 0.000233 + 40, wz * 0.000233 - 20, seed + 191, 4, 2, 0.55);
  const dist = Math.hypot(wx, wz);
  const spawnBias = dist < SPAWN_FLATTEN_RADIUS ? 0.18 : 0;
  const m = moist - spawnBias * 0.35;
  const t = temp + spawnBias * 0.25;

  // Warm + wet → swamp; cool/dry → rock; moist mid → forest; else grasslands
  if (m > 0.58 && t > 0.48) return BIOME.SWAMP;
  if (t < 0.4 || m < 0.34) return BIOME.ROCK_FIELDS;
  if (m > 0.5 && t > 0.38) return BIOME.FOREST;
  return BIOME.GRASSLANDS;
}

/**
 * Landform surface height — broad rolling hills only (no cliff lifts).
 * No caves. Spawn basin flattens toward SEA_LEVEL.
 * Hydro bank depressions are applied in {@link surfaceY}.
 *
 * fbm2D is ~[0,1] with mean ≈0.50 on these octaves. Gains are centered so
 * hills roll above sea without flooding half the map (static sea fill only
 * where landform is truly below SEA_LEVEL).
 */
export function noiseSurfaceY(
  wx: number,
  wz: number,
  seed: number,
  biomeHint?: BiomeId,
): number {
  // Broad + mid-scale rolls (no sparse 10–60 cliff spikes)
  const n = fbm2D(wx * 0.0024, wz * 0.0024, seed, 5, 2, 0.5);
  const mid = fbm2D(wx * 0.0065, wz * 0.0065, seed + 211, 3, 2.1, 0.48);
  const detail = fbm2D(wx * 0.014, wz * 0.014, seed + 313, 2, 2, 0.4);
  const biome = biomeHint ?? biomeAt(wx, wz, seed);

  // Full rescale: soft hills around SURFACE_TARGET (~15), hard cap MAX_LAND (~30).
  // Prior amps (34–42 @ SEA=48) would blow past land≤30 at SEA=10 — shrink ~4×.
  let rollAmp: number;
  let midAmp: number;
  let baseBias: number;
  if (biome === BIOME.ROCK_FIELDS) {
    rollAmp = 12;
    midAmp = 5;
    baseBias = 4;
  } else if (biome === BIOME.FOREST) {
    rollAmp = 10;
    midAmp = 4;
    baseBias = 3;
  } else if (biome === BIOME.SWAMP) {
    rollAmp = 4; // flatter wet lowlands
    midAmp = 2;
    baseBias = 1;
  } else {
    rollAmp = 9;
    midAmp = 4;
    baseBias = 3;
  }

  // Soft rolls only — no cliff lifts; land band y1–30
  let surface =
    SEA_LEVEL +
    baseBias +
    (n - 0.5) * rollAmp +
    (mid - 0.48) * midAmp +
    (detail - 0.47) * 1.5;
  surface = Math.floor(surface);

  const dist = Math.hypot(wx, wz);
  const r = SPAWN_FLATTEN_RADIUS;
  if (dist < r) {
    const t = dist / r;
    const w = t * t * (3 - 2 * t); // smoothstep
    // Hub eases toward SURFACE_TARGET (~15), not bare sea floor
    const hub = SURFACE_TARGET;
    surface = Math.floor(hub + (surface - hub) * w);
    if (surface < SEA_LEVEL) surface = SEA_LEVEL;
  }
  // Playtest 2.19: no below-sea sand disk around origin (stronger than flatten r)
  if (dist < SAND_ORIGIN_BAN && surface < SEA_LEVEL) {
    surface = SEA_LEVEL;
  }

  // Band law: never below bedrock+1 floor sample, never above max land
  return Math.max(2, Math.min(MAX_LAND_HEIGHT, surface));
}

/** True when landform top is beach/low sand (≤ sea−1), pre-hydro paint. */
export function isSandLandAt(wx: number, wz: number, seed: number): boolean {
  return noiseSurfaceY(wx, wz, seed) <= SEA_LEVEL - 1;
}

/** Hydro class: dry land, stream channel, or pond basin. */
export type HydroClass = 0 | 1 | 2; // 0=none, 1=stream, 2=pond

/**
 * Stream bank style 0..1 (playtest 2.21): low = steep cut, high = gradual ramp.
 * Slow fbm so whole reaches share a profile (not confetti per cell).
 */
export function streamBankStyleAt(wx: number, wz: number, seed: number): number {
  return fbm2D(wx * 0.0011 + 17, wz * 0.0011 - 9, seed + 960, 3, 2, 0.5);
}

/**
 * Soft-bank fade radius (blocks) for stream depression blend.
 * 0.2.53: steeper ≈4–5 · gradual ≈9–11 (was ~3–9) so banks ramp more gently.
 */
export function streamBankFadeAt(wx: number, wz: number, seed: number): number {
  const s = streamBankStyleAt(wx, wz, seed);
  return 4.0 + s * 7.0;
}

/**
 * How many blocks to sink hydro beds below surrounding landform.
 * Streams: deep incision scaled by height above sea so channels cut through hills
 * instead of riding the slope (playtest 2.5). Playtest 2.21: bank style modulates
 * depth slightly (steep = deeper cut). 0.2.52: beds ≈4–14 under sea10.
 * 0.2.53: slightly deeper beds ≈5–16 + wider soft-bank fade.
 * Ponds: edge shallow / center deep (playtest 2.11) — pass `pondCenterT` in 0..1
 * (1 = dead center) when known.
 * Optional `landY` is the landform top at the hydro cell (defaults SEA_LEVEL).
 * Optional `bankStyle` 0..1 (steep→gradual); omit to sample at call site.
 */
export function hydroBankDepth(
  hydro: HydroClass,
  landY: number = SEA_LEVEL,
  pondCenterT: number = 0.55,
  bankStyle?: number,
): number {
  if (hydro === 1) {
    const above = Math.max(0, landY - SEA_LEVEL);
    // 0.2.53: cut 5–16 (was 4–14) so channels stay wetter under sea10
    let d = Math.min(16, 6 + Math.floor(above * 0.72));
    // Steep banks bite a little deeper; gradual leave a softer floor
    if (bankStyle !== undefined) {
      d = Math.min(17, Math.max(5, d + Math.floor((0.5 - bankStyle) * 3)));
    }
    return d;
  }
  if (hydro === 2) {
    // Full rescale: shore ~2–3, center ~6–10 (was up to 32 — oversized vs land≤30)
    const t = Math.max(0, Math.min(1, pondCenterT));
    const bowl = t * t; // bias depth toward true center
    return Math.min(10, 2 + Math.floor(bowl * 8));
  }
  return 0;
}

/**
 * Final walkable surface Y: rolling landform minus hydro bank depression.
 * Streams incise through hills (deep cut + thalweg smoothing so beds don't
 * climb local peaks). Soft banks fade with style-dependent radius (playtest
 * 2.21 steep vs gradual). Ponds: center-deep bowl with floor continuity so
 * beds never ride local rises (playtest 2.11). Prefer this over
 * {@link noiseSurfaceY} for placement that must match columns.
 *
 * Optional `landHint` skips a redundant landform eval. Optional grid lookups
 * (playtest 2.7 chunk gen) avoid O(neighborhood) fbm when building a whole chunk.
 */
export function surfaceY(
  wx: number,
  wz: number,
  seed: number,
  landHint?: number,
  grids?: {
    landAt: (x: number, z: number) => number;
    hydroAt: (x: number, z: number) => HydroClass;
  },
): number {
  const land = landHint ?? grids?.landAt(wx, wz) ?? noiseSurfaceY(wx, wz, seed);
  if (land < SEA_LEVEL) return land;

  const selfH = grids?.hydroAt(wx, wz) ?? hydroAt(wx, wz, seed);
  const selfPondT = selfH === 2 ? pondCenterTAt(wx, wz, seed) : 0;
  const selfBank =
    selfH === 1 ? streamBankStyleAt(wx, wz, seed) : undefined;
  // Soft bank: max depression from self + nearby hydro (streams fade by style, ponds ~4)
  let sink = 0;
  const R = 12; // 0.2.53: cover gradual fade up to ~11
  for (let dz = -R; dz <= R; dz++) {
    for (let dx = -R; dx <= R; dx++) {
      const hx = wx + dx;
      const hz = wz + dz;
      const hh = grids?.hydroAt(hx, hz) ?? hydroAt(hx, hz, seed);
      if (hh === 0) continue;
      const nLand = grids?.landAt(hx, hz) ?? noiseSurfaceY(hx, hz, seed);
      const pondT = hh === 2 ? pondCenterTAt(hx, hz, seed) : 0;
      const bankS = hh === 1 ? streamBankStyleAt(hx, hz, seed) : undefined;
      const d = hydroBankDepth(hh, nLand, pondT, bankS);
      if (d <= 0) continue;
      const dist = Math.hypot(dx, dz);
      const fade =
        hh === 1
          ? bankS !== undefined
            ? 4.0 + bankS * 7.0
            : 7.0
          : 4.2;
      const partial = Math.floor(d * Math.max(0, 1 - dist / fade));
      if (partial > sink) sink = partial;
    }
  }
  if (sink <= 0) return land;

  let y = land - sink;

  // Thalweg continuity: on-stream cells pull toward the lowest nearby channel floor
  // so ridges don't force the bed uphill through a hill crest.
  if (selfH === 1) {
    let minBed = y;
    for (let dz = -3; dz <= 3; dz++) {
      for (let dx = -3; dx <= 3; dx++) {
        if (dx === 0 && dz === 0) continue;
        const hx = wx + dx;
        const hz = wz + dz;
        if ((grids?.hydroAt(hx, hz) ?? hydroAt(hx, hz, seed)) !== 1) continue;
        const nLand = grids?.landAt(hx, hz) ?? noiseSurfaceY(hx, hz, seed);
        const nBank = streamBankStyleAt(hx, hz, seed);
        const nBed = nLand - hydroBankDepth(1, nLand, 0, nBank);
        if (nBed < minBed) minBed = nBed;
      }
    }
    y = Math.min(y, Math.floor(minBed + 1));
    // Self floor with bank style
    y = Math.min(y, land - hydroBankDepth(1, land, 0, selfBank));
  }

  // Pond floor continuity (playtest 2.11/2.14): never let the basin ride a
  // local land rise — pull toward the lowest nearby pond bed + deeper bowl.
  // Also sit below the lowest neighboring *dry* land so ponds read as true
  // depressions in surrounding lows (Rich playtest).
  if (selfH === 2) {
    let minBed = y;
    let minDryLand = land;
    for (let dz = -5; dz <= 5; dz++) {
      for (let dx = -5; dx <= 5; dx++) {
        if (dx === 0 && dz === 0) continue;
        const hx = wx + dx;
        const hz = wz + dz;
        const nLand = grids?.landAt(hx, hz) ?? noiseSurfaceY(hx, hz, seed);
        const nh = grids?.hydroAt(hx, hz) ?? hydroAt(hx, hz, seed);
        if (nh === 2) {
          const nT = pondCenterTAt(hx, hz, seed);
          const nBed = nLand - hydroBankDepth(2, nLand, nT);
          if (nBed < minBed) minBed = nBed;
        } else if (nh === 0 && nLand >= SEA_LEVEL) {
          if (nLand < minDryLand) minDryLand = nLand;
        }
      }
    }
    // Stronger pull near center so the bowl stays recessed
    const pull = selfPondT > 0.35 ? 0 : 1;
    y = Math.min(y, Math.floor(minBed + pull));
    // Floor at least 3 below the lowest nearby dry shore ring
    y = Math.min(y, minDryLand - 3 - Math.floor(selfPondT * 8));
    // Extra center bite so middle is visibly deeper than shore
    if (selfPondT > 0.45) {
      y = Math.min(y, land - hydroBankDepth(2, land, selfPondT));
    }
  }

  // Playtest 2.19: dry land inside hub sand-ban never sinks into beach/SAND.
  // Streams/ponds still carve (beds + water); soft banks stay at/above sea.
  if (selfH === 0 && Math.hypot(wx, wz) < SAND_ORIGIN_BAN && y < SEA_LEVEL) {
    y = SEA_LEVEL;
  }

  // Keep a walkable floor above bedrock (y0); sea-static water may still sit
  // above beds when incision drops under SEA_LEVEL (painted in generateChunk).
  // Full rescale: floor ≥1 (dirt above bedrock), cap at max land.
  return Math.max(1, Math.min(MAX_LAND_HEIGHT, y));
}

/** No pond basins inside this radius of origin (playtest 2.6 hub keep-out). */
export const POND_ORIGIN_KEEPOUT = 500;

/** Max pond diameter in blocks (playtest 2.11 — was up to ~3000). */
export const POND_MAX_DIAMETER = 980;

/**
 * Best matching pond site for (wx,wz), or null if outside every basin.
 * `centerT` is 1 at the geometric center and 0 at the shoreline (after wobble).
 */
function pondSiteAt(
  wx: number,
  wz: number,
  seed: number,
): { cx: number; cz: number; radius: number; dist: number; centerT: number } | null {
  const hubDist = Math.hypot(wx, wz);
  if (hubDist < POND_ORIGIN_KEEPOUT) return null;

  // Spacing keeps neighbors from merging into inland seas
  const cell = 1600;
  const gx0 = Math.floor(wx / cell);
  const gz0 = Math.floor(wz / cell);

  let best: {
    cx: number;
    cz: number;
    radius: number;
    dist: number;
    centerT: number;
  } | null = null;

  for (let gz = gz0 - 1; gz <= gz0 + 1; gz++) {
    for (let gx = gx0 - 1; gx <= gx0 + 1; gx++) {
      // ~50% of cells host a pond
      const host = rand2(gx * 17.1, gz * 13.7, seed + 870);
      if (host < 0.5) continue;

      const cx =
        gx * cell +
        cell * (0.18 + 0.64 * rand2(gx + 0.3, gz - 0.2, seed + 871));
      const cz =
        gz * cell +
        cell * (0.18 + 0.64 * rand2(gx - 0.4, gz + 0.5, seed + 872));

      // Skip centers that would pull the basin into the hub keep-out
      if (Math.hypot(cx, cz) < POND_ORIGIN_KEEPOUT + 120) continue;

      // Radius 90–490 → diameter 180–980 (hard cap under 1000)
      const rRoll = rand2(gx * 3.1, gz * 5.9, seed + 873);
      const radius = Math.min(
        Math.floor(POND_MAX_DIAMETER / 2),
        90 + Math.floor(rRoll * rRoll * 400),
      );

      const dx = wx - cx;
      const dz = wz - cz;
      const dist = Math.hypot(dx, dz);
      if (dist > radius + 40) continue;

      // Irregular shoreline: ±12% radius wobble
      const ang = Math.atan2(dz, dx);
      const shore =
        0.88 +
        0.24 *
          fbm2D(
            Math.cos(ang) * 2.1 + cx * 0.001,
            Math.sin(ang) * 2.1 + cz * 0.001,
            seed + 874 + gx + gz * 19,
            3,
            2,
            0.55,
          );
      const rEff = radius * shore;
      if (dist > rEff) continue;

      const centerT = Math.max(0, Math.min(1, 1 - dist / Math.max(1, rEff)));
      if (!best || centerT > best.centerT) {
        best = { cx, cz, radius, dist, centerT };
      }
    }
  }
  return best;
}

/**
 * Large pond basin membership (playtest 2.6 / 2.11 size cap).
 * Sparse grid sites → diameter ~180–~980. Never inside
 * {@link POND_ORIGIN_KEEPOUT} of origin. Soft irregular shoreline via fbm.
 */
function pondBasinAt(wx: number, wz: number, seed: number): boolean {
  return pondSiteAt(wx, wz, seed) !== null;
}

/** 0 at shore … 1 at pond center (0 outside). Used for bowl depth. */
function pondCenterTAt(wx: number, wz: number, seed: number): number {
  return pondSiteAt(wx, wz, seed)?.centerT ?? 0;
}

/**
 * Nearest pond geometry for path detours — includes exterior influence band.
 * Unlike {@link pondSiteAt}, returns ponds when the sample is outside the
 * basin but within `reach` of the effective shoreline (for envelope routing).
 */
function nearestPondGeom(
  wx: number,
  wz: number,
  seed: number,
  reach: number,
): { cx: number; cz: number; rEff: number; dist: number } | null {
  const cell = 1600;
  const gx0 = Math.floor(wx / cell);
  const gz0 = Math.floor(wz / cell);
  let best: { cx: number; cz: number; rEff: number; dist: number } | null = null;
  let bestKey = Infinity;

  for (let gz = gz0 - 1; gz <= gz0 + 1; gz++) {
    for (let gx = gx0 - 1; gx <= gx0 + 1; gx++) {
      const host = rand2(gx * 17.1, gz * 13.7, seed + 870);
      if (host < 0.5) continue;
      const cx =
        gx * cell +
        cell * (0.18 + 0.64 * rand2(gx + 0.3, gz - 0.2, seed + 871));
      const cz =
        gz * cell +
        cell * (0.18 + 0.64 * rand2(gx - 0.4, gz + 0.5, seed + 872));
      if (Math.hypot(cx, cz) < POND_ORIGIN_KEEPOUT + 120) continue;
      const rRoll = rand2(gx * 3.1, gz * 5.9, seed + 873);
      const radius = Math.min(
        Math.floor(POND_MAX_DIAMETER / 2),
        90 + Math.floor(rRoll * rRoll * 400),
      );
      const dx = wx - cx;
      const dz = wz - cz;
      const dist = Math.hypot(dx, dz);
      if (dist > radius + reach + 48) continue;
      const ang = Math.atan2(dz, dx);
      const shore =
        0.88 +
        0.24 *
          fbm2D(
            Math.cos(ang) * 2.1 + cx * 0.001,
            Math.sin(ang) * 2.1 + cz * 0.001,
            seed + 874 + gx + gz * 19,
            3,
            2,
            0.55,
          );
      const rEff = radius * shore;
      const pastShore = dist - rEff;
      if (pastShore > reach) continue;
      // Prefer ponds that most constrain the sample (deepest penetration / nearest)
      const key = pastShore;
      if (key < bestKey) {
        bestKey = key;
        best = { cx, cz, rEff, dist };
      }
    }
  }
  return best;
}

/** True when dry-road paint must not occupy this column (sand beach or pond). */
function pathBlockedAt(wx: number, wz: number, seed: number): boolean {
  if (isSandLandAt(wx, wz, seed)) return true;
  if (pondBasinAt(wx, wz, seed)) return true;
  return false;
}

/**
 * Continuous lateral detour (blocks along artery normal) so the centerline
 * skirts pond envelopes + sand beaches instead of gapping through them.
 * Returns NaN when no clear offset exists within {@link DETOUR_SEARCH_MAX}.
 * Fast-path 0 when foot is clear of sand/pond influence.
 */
function arteryObstacleDetour(
  footX: number,
  footZ: number,
  uAx: number,
  uAz: number,
  c: number,
  s: number,
  halfW: number,
  seed: number,
  preferSign: number,
): number {
  const clearR = halfW + POND_PATH_BUFFER;
  const sandBuf = halfW + SAND_PATH_BUFFER * 0.55;

  // Cheap threat scan — most artery samples hit this and return 0.
  const pondThreat = nearestPondGeom(footX, footZ, seed, clearR + halfW + 64);
  let sandThreat = pathBlockedAt(footX, footZ, seed);
  if (!sandThreat) {
    const sb = SAND_PATH_BUFFER + halfW * 0.5;
    if (
      isSandLandAt(footX + uAx * sb, footZ + uAz * sb, seed) ||
      isSandLandAt(footX - uAx * sb, footZ - uAz * sb, seed) ||
      isSandLandAt(footX + c * sb, footZ + s * sb, seed) ||
      isSandLandAt(footX - c * sb, footZ - s * sb, seed)
    ) {
      sandThreat = true;
    }
  }
  if (!pondThreat && !sandThreat) return 0;

  const stripClear = (lat: number): boolean => {
    const x = footX + uAx * lat;
    const z = footZ + uAz * lat;
    // Center + lateral shoulders of the road strip
    const probes: ReadonlyArray<readonly [number, number]> = [
      [0, 0],
      [uAx * halfW * 0.55, uAz * halfW * 0.55],
      [-uAx * halfW * 0.55, -uAz * halfW * 0.55],
      [c * 12, s * 12],
      [-c * 12, -s * 12],
    ];
    for (const [px, pz] of probes) {
      if (pathBlockedAt(x + px, z + pz, seed)) return false;
    }
    // Must also sit outside pond envelope (not merely outside painted basin)
    const pond = nearestPondGeom(x, z, seed, clearR + 8);
    if (pond && pond.dist < pond.rEff + POND_PATH_BUFFER + halfW * 0.35) {
      return false;
    }
    // Soft sand fringe — keep strip off beach edge
    if (
      isSandLandAt(x + uAx * sandBuf * 0.35, z + uAz * sandBuf * 0.35, seed) ||
      isSandLandAt(x - uAx * sandBuf * 0.35, z - uAz * sandBuf * 0.35, seed)
    ) {
      return false;
    }
    return true;
  };

  // Geometric pond envelope: push to tangent clear distance (continuous beltway)
  let geom = 0;
  const pond = pondThreat;
  if (pond) {
    const pondAlong = pond.cx * c + pond.cz * s;
    const pondAcross = -pond.cx * s + pond.cz * c;
    const footAlong = footX * c + footZ * s;
    const footAcross = -footX * s + footZ * c;
    const alongDist = Math.abs(footAlong - pondAlong);
    const need = pond.rEff + POND_PATH_BUFFER + halfW;
    // Only warp when the artery is in the along-window that would clip the pond
    if (alongDist < need + 96) {
      const delta = footAcross - pondAcross;
      const side =
        Math.abs(delta) > 1e-6
          ? delta >= 0
            ? 1
            : -1
          : preferSign >= 0
            ? 1
            : -1;
      // Chord half-width at this along station (circle clearance)
      const chord = Math.sqrt(Math.max(0, need * need - alongDist * alongDist));
      const minClear = chord > 0 ? chord : need * 0.25;
      if (Math.abs(delta) < minClear) {
        geom = side * minClear - delta;
      }
    }
  }

  if (stripClear(geom)) return geom;

  // Sand / residual block: search nearest lateral clear offset (prefer geom side)
  const pref = preferSign >= 0 ? 1 : -1;
  const geomSide = geom === 0 ? pref : geom >= 0 ? 1 : -1;
  for (let d = DETOUR_SEARCH_STEP; d <= DETOUR_SEARCH_MAX; d += DETOUR_SEARCH_STEP) {
    const a = geom + geomSide * d;
    if (stripClear(a)) return a;
    const b = geom - geomSide * d;
    if (stripClear(b)) return b;
  }
  // Prefer side from undeviated foot if geom side was wrong for a sand bar
  for (let d = DETOUR_SEARCH_STEP; d <= DETOUR_SEARCH_MAX; d += DETOUR_SEARCH_STEP) {
    const a = pref * d;
    if (Math.abs(a - geom) < 1) continue;
    if (stripClear(a)) return a;
    const b = -pref * d;
    if (Math.abs(b - geom) < 1) continue;
    if (stripClear(b)) return b;
  }
  return NaN;
}

/**
 * Push sample coords away from nearby rock-garden cells so ridge channels
 * bend around banned biomes instead of dead-ending on the border (playtest 2.9).
 */
function rockGardenRepulsion(
  wx: number,
  wz: number,
  seed: number,
): { sx: number; sz: number } {
  const R = 56;
  let pdx = 0;
  let pdz = 0;
  // 8-neighborhood samples; each rock hit pushes opposite the sample vector
  const offs: readonly (readonly [number, number])[] = [
    [R, 0],
    [-R, 0],
    [0, R],
    [0, -R],
    [R, R],
    [-R, -R],
    [R, -R],
    [-R, R],
  ];
  for (const [ox, oz] of offs) {
    if (biomeAt(wx + ox, wz + oz, seed) === BIOME.ROCK_FIELDS) {
      pdx -= ox;
      pdz -= oz;
    }
  }
  if (pdx === 0 && pdz === 0) return { sx: wx, sz: wz };
  const len = Math.hypot(pdx, pdz);
  // ~0.55 of unit push, capped — enough to warp ridges around gardens
  const scale = Math.min(72, 0.55 * R) / len;
  return { sx: wx + pdx * scale, sz: wz + pdz * scale };
}

/**
 * True if a large pond basin is within `r` of (wx,wz) — used for clean
 * stream→pond mouths (channel may approach; pond class still wins inside).
 */
function nearPondBasin(wx: number, wz: number, seed: number, r: number): boolean {
  if (pondBasinAt(wx, wz, seed)) return true;
  // Cheap ring samples (full basin scan is already neighborhood-based)
  const step = Math.max(16, Math.floor(r / 2));
  for (let dz = -r; dz <= r; dz += step) {
    for (let dx = -r; dx <= r; dx += step) {
      if (dx * dx + dz * dz > r * r) continue;
      if (pondBasinAt(wx + dx, wz + dz, seed)) return true;
    }
  }
  return false;
}

/**
 * Legacy 2.9 ridge/noise streams — kept **only in swamp biomes** (playtest 2.10).
 * Wet floors stay a bit fragmented/meander-braided; non-swamp uses arteries.
 */
function swampRidgeStreamAt(wx: number, wz: number, seed: number): HydroClass {
  const { sx, sz } = rockGardenRepulsion(wx, wz, seed);

  const region = fbm2D(sx * 0.0016 + 41, sz * 0.0016 - 19, seed + 889, 3, 2, 0.5);
  if (region < 0.46) return 0;

  const a1 = fbm2D(sx * 0.0055 + 3, sz * 0.0055 - 7, seed + 890, 4, 2.1, 0.52);
  const a2 = fbm2D(sx * 0.0055 - 11, sz * 0.0055 + 17, seed + 895, 4, 2.1, 0.52);
  const b = fbm2D(sx * 0.01 - 14, sz * 0.01 + 5, seed + 891, 3, 2, 0.5);
  const r1 = Math.abs(a1 - 0.5);
  const r2 = Math.abs(a2 - 0.5);
  const ridge = Math.min(r1, r2);
  const cross = Math.abs(b - 0.5);
  const confluence = r1 < 0.055 && r2 < 0.055;

  const mouth = nearPondBasin(wx, wz, seed, 48);
  let mainW = mouth ? 0.048 : 0.04;
  let bankW = mouth ? 0.034 : 0.028;
  let crossMin = mouth ? 0.032 : 0.038;
  if (confluence) {
    mainW += 0.014;
    bankW += 0.01;
    crossMin *= 0.9;
  }

  if (ridge < mainW && cross > crossMin) return 1;
  if (ridge < bankW && cross > crossMin * 0.85) return 1;
  return 0;
}

/**
 * Shared stream artery definitions (playtest 2.10+). Keep hydro paint and
 * nearestStreamGeom in lockstep.
 */
const STREAM_ARTERIES: ReadonlyArray<{
  seedOff: number;
  baseAngle: number;
  centerOff: number;
  presenceMin: number;
  throughHub?: boolean;
  hubRadius?: number;
}> = [
  // 0.2.54: 6→3 arteries (~50% fewer corridors). One hub feeder + two wild feeders.
  // Continuous feeder near spawn so hub wilds always see a coherent channel.
  {
    seedOff: 801,
    baseAngle: 0.62,
    centerOff: 110,
    presenceMin: 0.3,
    throughHub: true,
    // Tighter than 420 so forced paint doesn't dominate the whole near-origin ring
    hubRadius: 280,
  },
  // Higher presenceMin → secondary corridors rarer (extra ~half beyond artery cut)
  { seedOff: 821, baseAngle: 1.12, centerOff: 390, presenceMin: 0.52 },
  { seedOff: 831, baseAngle: 0.28, centerOff: -510, presenceMin: 0.55 },
];

/**
 * 0.2.54: secondaries stay out of the origin disk so spawn doesn't get multi-artery
 * scribble / dual beds / weird confluence around world (0,0). Primary still feeds hub.
 */
const STREAM_ORIGIN_SECONDARY_BAN = 220;

/** Base half-width scale — 2.15 was 9.3; 2.21 +20% → ~11.2 (full ~18–29). */
export const STREAM_HALF_W_BASE = 11.16;

/**
 * 0.2.53: when a secondary runs roughly parallel to another artery within this
 * centerline separation (voxels), terminate it except at a T-join apron.
 * Wider corridor + looser angle than 0.2.52 (280/0.38) to kill lingering dual runs.
 */
export const STREAM_PARALLEL_SEP = 360;
/** Max |Δangle| (rad) to treat two arteries as parallel for merge/terminate. */
const STREAM_PARALLEL_ANGLE = 0.52;

type StreamArteryHit = {
  dist: number;
  /** Signed distance to centerline (across − offset − meander). */
  signedDist: number;
  primary: boolean;
  angle: number;
  s: number;
  c: number;
  seedOff: number;
};

/**
 * Evaluate all stream arteries at a rock-repelled sample. Shared by hydro
 * paint and path buffer geometry.
 */
function collectStreamArteryHits(
  sx: number,
  sz: number,
  seed: number,
): StreamArteryHit[] {
  const hubDist = Math.hypot(sx, sz);
  const hits: StreamArteryHit[] = [];

  for (const art of STREAM_ARTERIES) {
    // 0.2.54: only the hub feeder paints inside the origin ban (kills near-0 dual/scribble)
    if (art.throughHub !== true && hubDist < STREAM_ORIGIN_SECONDARY_BAN) {
      continue;
    }

    const angN = fbm2D(
      sx * 0.0003 + art.seedOff * 0.01,
      sz * 0.0003 - art.seedOff * 0.007,
      seed + art.seedOff,
      3,
      2,
      0.5,
    );
    // 0.2.52: slightly stronger bearing drift + multi-scale meander
    const angle = art.baseAngle + (angN - 0.5) * 0.62;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const along = sx * c + sz * s;
    const across = -sx * s + sz * c;

    // Meander: larger primary sine + mid fbm + short wiggle (still continuous)
    let meander =
      Math.sin(along * 0.0062 + art.seedOff * 0.02) * 42 +
      Math.sin(along * 0.013 + art.seedOff * 0.11) * 14 +
      (fbm2D(along * 0.0036, art.seedOff * 0.13, seed + art.seedOff + 5, 3, 2, 0.5) -
        0.5) *
        28;

    if (hubDist > 40) {
      const probe = 20;
      const left = rockMassField(sx - s * probe, sz + c * probe, seed);
      const right = rockMassField(sx + s * probe, sz - c * probe, seed);
      const push = (right - left) * 18;
      meander += art.throughHub ? push * 0.5 : push;
    }

    const signedDist = across - art.centerOff - meander;
    const dist = Math.abs(signedDist);

    const forceHub =
      art.throughHub === true && hubDist <= (art.hubRadius ?? 280);
    if (!forceHub) {
      const presence = fbm2D(
        along * 0.00095 + art.seedOff,
        art.centerOff * 0.002,
        seed + art.seedOff + 11,
        3,
        2,
        0.5,
      );
      if (presence < art.presenceMin) continue;
    }

    hits.push({
      dist,
      signedDist,
      primary: art.throughHub === true,
      angle,
      s,
      c,
      seedOff: art.seedOff,
    });
  }
  return hits;
}

/** Smallest absolute angle delta in [0, π]. */
function angleDeltaAbs(a: number, b: number): number {
  let d = Math.abs(a - b) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  // Parallel if same or opposite bearing
  return Math.min(d, Math.PI - d);
}

/**
 * Half-width at sample after mouth / multi-artery confluence widen (2.21).
 * 0.2.52: along-channel width variance ±20% around STREAM_HALF_W_BASE.
 */
function streamHalfWidthAt(
  sx: number,
  sz: number,
  seed: number,
  mouth: boolean,
  hits: ReadonlyArray<{ dist: number }>,
): number {
  // 0.8 + 0.4*wN → [0.8, 1.2] = ±20% of base
  const wN = fbm2D(sx * 0.0022, sz * 0.0022, seed + 840, 2, 2, 0.5);
  let halfW = STREAM_HALF_W_BASE * (0.8 + 0.4 * wN);
  if (mouth) halfW += 4.0;

  // Sort distances — confluence when 2+ arteries near the bed apron
  const dists = hits.map((h) => h.dist).sort((a, b) => a - b);
  const best = dists[0] ?? Infinity;
  const second = dists[1] ?? Infinity;
  const third = dists[2] ?? Infinity;

  // Clean T / multi-join: widen shared bed (no X punch-through — filter is separate)
  if (second < halfW * 1.4 && best < halfW * 1.7) halfW += 5.0;
  if (third < halfW * 1.55 && second < halfW * 1.5) halfW += 3.2;
  return halfW;
}

/**
 * Provisional half-width (no confluence widen) — shared by T-merge + parallel filters.
 */
function streamHalfWidth0(sx: number, sz: number, seed: number, mouth: boolean): number {
  const wN = fbm2D(sx * 0.0022, sz * 0.0022, seed + 840, 2, 2, 0.5);
  let halfW0 = STREAM_HALF_W_BASE * (0.8 + 0.4 * wN);
  if (mouth) halfW0 += 4.0;
  return halfW0;
}

/**
 * Filter artery hits: T-merge / no X-cross (2.16+) and parallel-run terminate (0.2.53).
 * Keeps hydro paint and nearestStreamGeom in lockstep.
 */
function filterStreamArteryHits(
  rawHits: ReadonlyArray<StreamArteryHit>,
  halfW0: number,
): StreamArteryHit[] {
  let primaryDist = Infinity;
  let primary: StreamArteryHit | null = null;
  for (const h of rawHits) {
    if (h.primary && h.dist < primaryDist) {
      primaryDist = h.dist;
      primary = h;
    }
  }

  const hits: StreamArteryHit[] = [];
  for (const h of rawHits) {
    if (!h.primary && primaryDist < Infinity) {
      // T-merge / no X-cross: secondary never owns primary core; apron join only
      // 0.2.53: slightly wider exclusive core + apron so X-cross / dual beds die sooner
      if (primaryDist < halfW0 * 0.55) continue;
      if (primaryDist < halfW0 * 1.08 && h.dist > halfW0 * 1.0) continue;

      // Parallel merge/terminate: primary centerline within STREAM_PARALLEL_SEP
      // and bearings align → drop secondary unless at a true T-apron.
      if (
        primary &&
        primaryDist < STREAM_PARALLEL_SEP &&
        angleDeltaAbs(h.angle, primary.angle) < STREAM_PARALLEL_ANGLE
      ) {
        const sep = Math.abs(h.signedDist - primary.signedDist);
        // Tighter join window so lingering dual channels don't count as a meet
        const nearJoin =
          primaryDist <= halfW0 * 1.25 && h.dist <= halfW0 * 1.35;
        // Parallel corridor (not a meeting): kill secondary
        if (sep < STREAM_PARALLEL_SEP && sep > halfW0 * 0.55 && !nearJoin) {
          continue;
        }
      }

      // 0.2.53: acute / near-orthogonal punch-through — if both beds are close
      // but secondary is outside the T-apron, kill secondary (no X-cross paint)
      if (
        primary &&
        primaryDist < halfW0 * 1.15 &&
        h.dist < halfW0 * 1.15 &&
        angleDeltaAbs(h.angle, primary.angle) >= STREAM_PARALLEL_ANGLE
      ) {
        // Allow only when secondary is clearly the feeder apron (closer to primary bed)
        if (h.dist > halfW0 * 0.95) continue;
      }
    }
    hits.push(h);
  }

  // Secondary–secondary: if two non-primaries run parallel within SEP, keep nearer only
  if (hits.length > 1) {
    const kept: StreamArteryHit[] = [];
    for (let i = 0; i < hits.length; i++) {
      const a = hits[i]!;
      if (a.primary) {
        kept.push(a);
        continue;
      }
      let drop = false;
      for (let j = 0; j < hits.length; j++) {
        if (i === j) continue;
        const b = hits[j]!;
        if (b.primary) continue;
        if (angleDeltaAbs(a.angle, b.angle) >= STREAM_PARALLEL_ANGLE) continue;
        const sep = Math.abs(a.signedDist - b.signedDist);
        if (sep >= STREAM_PARALLEL_SEP || sep <= halfW0 * 0.55) continue;
        const aJoin = a.dist <= halfW0 * 1.35 && b.dist <= halfW0 * 1.35;
        if (aJoin) continue;
        // Prefer lower dist, then lower seedOff
        if (
          b.dist < a.dist - 0.01 ||
          (Math.abs(b.dist - a.dist) <= 0.01 && b.seedOff < a.seedOff)
        ) {
          drop = true;
          break;
        }
      }
      if (!drop) kept.push(a);
    }
    return kept;
  }
  return hits;
}

/**
 * Road-like continuous stream arteries with light wander (playtest 2.10).
 * Same elongated-corridor idea as {@link pathLaneAt}, but:
 * - more meander / bearing drift than roads (wander a little, not scribble)
 * - routes around rock gardens via repulsion warp + rock-mass nudge
 * - soft widen at pond mouths and at clean multi-artery confluence
 *
 * Playtest 2.16: **no X-cross** — secondary arteries terminate into the
 * primary hub feeder (T-merge). Primary owns the channel core; secondaries
 * may only meet at the apron, never punch through.
 *
 * Playtest 2.21: width **+20%**; extra feeders; multi-artery confluence widen
 * when 2–3 arteries meet (still no X-cross through primary core).
 *
 * 0.2.52: stronger meander; halfW ±20%; parallel-run merge/terminate ~280 vx.
 * 0.2.53: SEP=360 ANGLE=0.52; tighter T-apron; acute punch-through kill; beds 5–16.
 * 0.2.54: arteries 6→3 (~50%); secondary presence harder; origin secondary ban 220;
 *         hub force radius 280 (was 420).
 */
function arteryStreamAt(wx: number, wz: number, seed: number): HydroClass {
  const { sx, sz } = rockGardenRepulsion(wx, wz, seed);
  const mouth = nearPondBasin(wx, wz, seed, 48);
  const rawHits = collectStreamArteryHits(sx, sz, seed);
  if (rawHits.length === 0) return 0;

  const halfW0 = streamHalfWidth0(sx, sz, seed, mouth);
  const hits = filterStreamArteryHits(rawHits, halfW0);
  if (hits.length === 0) return 0;

  const halfW = streamHalfWidthAt(sx, sz, seed, mouth, hits);
  let best = Infinity;
  for (const h of hits) {
    if (h.dist < best) best = h.dist;
  }
  if (best <= halfW) return 1;
  return 0;
}

/**
 * Deterministic stream / pond field (above sea only used for beds).
 * Ponds = large wild basins (min ~400 wide); none within 500 of origin.
 *
 * Playtest 2.10:
 * - Non-swamp streams = **road-like arteries** with light wander (continuous).
 * - **Swamp** keeps 2.9 ridge/noise stream behavior (wet messy floors).
 * - Still hard-ban rock-garden interiors; route-around via repulsion; pond mouths.
 */
export function hydroAt(wx: number, wz: number, seed: number): HydroClass {
  // Large ponds first (playtest 2.6)
  if (pondBasinAt(wx, wz, seed)) return 2;

  // Rock garden interior: never paint streams (playtest 2.4 / 2.9 hard ban)
  if (biomeAt(wx, wz, seed) === BIOME.ROCK_FIELDS) return 0;

  // Swamp exception: keep prior ridge streams (playtest 2.10)
  if (biomeAt(wx, wz, seed) === BIOME.SWAMP) {
    return swampRidgeStreamAt(wx, wz, seed);
  }

  return arteryStreamAt(wx, wz, seed);
}

function surfaceBlockFor(
  surface: number,
  biome: BiomeId,
  wx = 0,
  wz = 0,
): number {
  // Beach / low basin — aqua sand (tint in BLOCK_COLOR). Hub disk: never SAND
  // (playtest 2.19 — landform + dry surfaceY already floored; belt-and-braces).
  if (surface <= SEA_LEVEL - 1) {
    if (Math.hypot(wx, wz) < SAND_ORIGIN_BAN) {
      // Prefer biome grass over beach inside origin ban
      if (biome === BIOME.FOREST) return BLOCK.FOREST_GRASS;
      if (biome === BIOME.SWAMP) return BLOCK.SWAMP_GRASS;
      if (biome === BIOME.ROCK_FIELDS) return BLOCK.GRASS;
      return BLOCK.GRASS;
    }
    return BLOCK.SAND;
  }
  if (biome === BIOME.ROCK_FIELDS) {
    // Rocky tops; thin grass on lower shelves (full rescale: was SEA+12 @ tall hills)
    return surface >= SEA_LEVEL + 8 ? BLOCK.ROCK : BLOCK.GRASS;
  }
  if (biome === BIOME.FOREST) return BLOCK.FOREST_GRASS;
  if (biome === BIOME.SWAMP) {
    return surface <= SEA_LEVEL + 1 ? BLOCK.SWAMP_GRASS : BLOCK.SWAMP_GRASS;
  }
  return BLOCK.GRASS;
}

/**
 * Column under surface — full rescale band law:
 * y0 = BEDROCK (indestructible floor); y1…surface-1 = dirt/mud/rock/stone fill.
 * Dirt skin stays shallow so the 1–30 band doesn't become pure dirt pillars.
 */
function fillUnderSurface(ly: number, surface: number, biome: BiomeId): number {
  if (ly <= 0) return BLOCK.BEDROCK;
  const dirtDepth = biome === BIOME.SWAMP ? 3 : 4;
  if (ly < surface - dirtDepth) return BLOCK.STONE;
  if (biome === BIOME.SWAMP) return BLOCK.MUD;
  if (biome === BIOME.ROCK_FIELDS && ly >= surface - 2) return BLOCK.ROCK;
  return BLOCK.DIRT;
}

/** Set block if in chunk and currently air (or replaceAirOnly false). */
function trySet(
  chunk: Chunk,
  lx: number,
  ly: number,
  lz: number,
  id: number,
  replaceAirOnly = true,
): void {
  if (
    lx < 0 ||
    lz < 0 ||
    ly < 0 ||
    lx >= CHUNK_SIZE ||
    lz >= CHUNK_SIZE ||
    ly >= CHUNK_HEIGHT
  ) {
    return;
  }
  // Never overwrite y=0 bedrock or any existing BEDROCK cell via props/scatter.
  if (ly <= 0) return;
  const cur = chunk.get(lx, ly, lz);
  if (cur === BLOCK.BEDROCK) return;
  if (replaceAirOnly && cur !== BLOCK.AIR && cur !== BLOCK.TALL_GRASS) return;
  chunk.set(lx, ly, lz, id);
}

/**
 * Broadleaf / oak — proportional multi-voxel (anti-lollipop).
 * 0.2.52: trunk+foliage total lands in 40–90 (trunk ≤60). Phase 2.3 thick trunks.
 */
function placeOakTree(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
  wx: number,
  wz: number,
  seed: number,
): void {
  // Trunk 30–52; canopy ~10–14 → total ≈40–66 (cap 90 / trunk≤60)
  let h = 30 + Math.floor(rand2(wx, wz, seed + 33) * 23); // 30–52
  // Trunk half-width scaled with h (thicker → informal shorten vs thin poles)
  let trunkHalf = h >= 46 ? 3 : h >= 38 ? 2 : 1;
  if (trunkHalf >= 3) h = Math.max(28, Math.floor(h * 0.92));
  else if (trunkHalf >= 2) h = Math.max(28, Math.floor(h * 0.96));
  if (h > 60) h = 60;
  // Canopy radius grows with height so crown is a plump ellipsoid, not a lollipop cap
  const canopyR = Math.max(6, Math.min(12, Math.floor(h * 0.26)));
  const canopyDepth = Math.max(10, Math.floor(canopyR * 1.05));
  if (h + canopyDepth < 40) h = 40 - canopyDepth;
  if (h + canopyDepth > TREE_MAX_TOTAL_HEIGHT) {
    h = Math.max(28, TREE_MAX_TOTAL_HEIGHT - canopyDepth);
  }
  if (h > 60) h = 60;
  if (surface + h + canopyDepth >= CHUNK_HEIGHT) return;
  trunkHalf = h >= 46 ? 3 : h >= 38 ? 2 : 1;

  for (let dy = 1; dy <= h; dy++) {
    // Slight flare at base
    const baseFlare = dy <= 2 ? 1 : 0;
    const th = trunkHalf + baseFlare;
    for (let dx = -th; dx <= th; dx++) {
      for (let dz = -th; dz <= th; dz++) {
        // Soft square trunk (nibble corners)
        if (Math.abs(dx) === th && Math.abs(dz) === th) {
          if (rand2(wx + dx, wz + dz + dy, seed + 34) > 0.4) continue;
        }
        trySet(chunk, lx + dx, surface + dy, lz + dz, BLOCK.LOG);
      }
    }
  }

  const canopyY = surface + h - Math.floor(canopyDepth * 0.25);
  const leaf = BLOCK.LEAVES;
  for (let dx = -canopyR; dx <= canopyR; dx++) {
    for (let dz = -canopyR; dz <= canopyR; dz++) {
      for (let dy = -canopyDepth; dy <= canopyR; dy++) {
        // Ellipsoid: stretch Y less than XZ so crown reads rounded, not flat disc
        const nx = dx / (canopyR + 0.35);
        const nz = dz / (canopyR + 0.35);
        const ny = dy / (canopyDepth + 0.35);
        if (nx * nx + nz * nz + ny * ny * 1.15 > 1.05) continue;
        // Keep trunk column clear under crown center
        if (Math.abs(dx) <= trunkHalf && Math.abs(dz) <= trunkHalf && dy < -1) continue;
        // Sparse outer fringe
        if (nx * nx + nz * nz > 0.72 && rand2(wx + dx, wz + dz + dy * 2, seed + 35) > 0.7) continue;
        trySet(chunk, lx + dx, canopyY + dy, lz + dz, leaf);
      }
    }
  }
}

/** Conifer — proportional layered cones. 0.2.52: total trunk+tip in 40–90. */
function placePineTree(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
  wx: number,
  wz: number,
  seed: number,
): void {
  // Trunk 34–56; tip foliage +6 → total ≈40–62 (cap 90 / trunk≤60)
  let h = 34 + Math.floor(rand2(wx, wz, seed + 44) * 23); // 34–56
  // Min 3×3; mid 5×5; tall pines 7×7 — thicker trunks shorten slightly
  let trunkHalf = h >= 48 ? 2 : 1;
  if (trunkHalf >= 2) h = Math.max(32, Math.floor(h * 0.94));
  if (h > 60) h = 60;
  const foliagePad = 6;
  if (h + foliagePad < 40) h = 40 - foliagePad;
  if (h + foliagePad > TREE_MAX_TOTAL_HEIGHT) {
    h = Math.max(32, TREE_MAX_TOTAL_HEIGHT - foliagePad);
  }
  if (h > 60) h = 60;
  if (surface + h + foliagePad >= CHUNK_HEIGHT) return;
  trunkHalf = h >= 48 ? 2 : 1;
  const baseRad = Math.max(4, Math.min(8, Math.floor(h * 0.15)));

  for (let dy = 1; dy <= h; dy++) {
    const baseFlare = dy <= 2 ? 1 : 0;
    const th = trunkHalf + baseFlare;
    for (let dx = -th; dx <= th; dx++) {
      for (let dz = -th; dz <= th; dz++) {
        if (Math.abs(dx) === th && Math.abs(dz) === th && th > 0) {
          if (rand2(wx + dx, wz + dz + dy, seed + 45) > 0.5) continue;
        }
        trySet(chunk, lx + dx, surface + dy, lz + dz, BLOCK.PINE_LOG);
      }
    }
  }

  // Layered cones from ~28% height to tip — radius scales with height
  const start = Math.floor(h * 0.28);
  const tip = h + foliagePad;
  for (let dy = start; dy <= tip; dy++) {
    const t = (dy - start) / Math.max(1, tip - start);
    const rad = Math.max(1, Math.floor(baseRad * (1 - t) + 0.5));
    const y = surface + dy;
    for (let dx = -rad; dx <= rad; dx++) {
      for (let dz = -rad; dz <= rad; dz++) {
        if (dx * dx + dz * dz > rad * rad + 0.8) continue;
        if (Math.abs(dx) <= trunkHalf && Math.abs(dz) <= trunkHalf && dy < h) continue;
        trySet(chunk, lx + dx, y, lz + dz, BLOCK.PINE_LEAVES);
      }
    }
  }
}

/** Willow — wide droopy crown. 0.2.52: total trunk+droop in 40–90. */
function placeWillowTree(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
  wx: number,
  wz: number,
  seed: number,
): void {
  // Trunk 26–44; crown + droop ~14–18 → total ≈40–62
  let h = 26 + Math.floor(rand2(wx, wz, seed + 55) * 19); // 26–44
  // Always at least 3×3; taller willows 5×5
  let trunkHalf = h >= 36 ? 2 : 1;
  if (trunkHalf >= 2) h = Math.max(24, Math.floor(h * 0.95));
  if (h > 60) h = 60;
  const canopyR = Math.max(8, Math.min(14, Math.floor(h * 0.34)));
  const canopyPad = Math.max(14, Math.floor(canopyR * 0.55) + 6);
  if (h + canopyPad < 40) h = 40 - canopyPad;
  if (h + canopyPad > TREE_MAX_TOTAL_HEIGHT) {
    h = Math.max(24, TREE_MAX_TOTAL_HEIGHT - canopyPad);
  }
  if (h > 60) h = 60;
  if (surface + h + canopyPad >= CHUNK_HEIGHT) return;
  trunkHalf = h >= 36 ? 2 : 1;

  for (let dy = 1; dy <= h; dy++) {
    const baseFlare = dy <= 2 ? 1 : 0;
    const th = trunkHalf + baseFlare;
    for (let dx = -th; dx <= th; dx++) {
      for (let dz = -th; dz <= th; dz++) {
        if (Math.abs(dx) === th && Math.abs(dz) === th) {
          if (rand2(wx + dx, wz + dz + dy, seed + 56) > 0.45) continue;
        }
        trySet(chunk, lx + dx, surface + dy, lz + dz, BLOCK.WILLOW_LOG);
      }
    }
  }

  const canopyY = surface + h;
  for (let dx = -canopyR; dx <= canopyR; dx++) {
    for (let dz = -canopyR; dz <= canopyR; dz++) {
      for (let dy = -3; dy <= Math.floor(canopyR * 0.5); dy++) {
        const nx = dx / (canopyR + 0.2);
        const nz = dz / (canopyR + 0.2);
        const ny = dy / (canopyR * 0.5 + 0.2);
        if (nx * nx + nz * nz + ny * ny * 0.55 > 1.0) continue;
        if (Math.abs(dx) <= trunkHalf && Math.abs(dz) <= trunkHalf && dy <= 0) continue;
        trySet(chunk, lx + dx, canopyY + dy, lz + dz, BLOCK.WILLOW_LEAVES);
      }
      // Droop strands on outer ring
      const manh = Math.abs(dx) + Math.abs(dz);
      if (manh >= Math.floor(canopyR * 0.45) && manh <= canopyR) {
        const droop = 3 + Math.floor(rand2(wx + dx, wz + dz, seed + 66) * 6);
        for (let dy = 1; dy <= droop; dy++) {
          trySet(chunk, lx + dx, canopyY - dy, lz + dz, BLOCK.WILLOW_LEAVES);
        }
      }
    }
  }
}

/**
 * Local rock-mass field used to gently bend roads around outcrops (playtest 2.6).
 * High in rock-fields biome and near deterministic boulder sites — not a full prop sim.
 */
function rockMassField(wx: number, wz: number, seed: number): number {
  let m = 0;
  if (biomeAt(wx, wz, seed) === BIOME.ROCK_FIELDS) {
    m = 0.55 + 0.45 * fbm2D(wx * 0.02, wz * 0.02, seed + 940, 3, 2, 0.5);
  } else {
    // Sparse grassland/forest boulder patches
    const p = fbm2D(wx * 0.011 + 9, wz * 0.011 - 4, seed + 941, 3, 2, 0.5);
    if (p > 0.72) m = (p - 0.72) * 3.2;
  }
  return Math.max(0, Math.min(1, m));
}

/**
 * Boulder — mostly low and wide (W/D > H), rough irregular silhouette.
 * Phase 2.3: kill plinth look; height typically 3–14, footprint wider than tall.
 * Never spill rock voxels into streams, ponds, or any road/path lane (core +
 * shoulder). Rock-garden density lives in the scatter gate, not here.
 * Roads keep priority (playtest 2.6 / 2.8).
 */
function placeRock(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
  wx: number,
  wz: number,
  seed: number,
): void {
  // Height mostly short; rare taller outcrops still wider than tall
  const roll = rand2(wx, wz, seed + 88);
  const h =
    roll < 0.7
      ? 3 + Math.floor(rand2(wx, wz, seed + 89) * 6) // 3–8 majority
      : 6 + Math.floor(rand2(wx, wz, seed + 90) * 9); // 6–14 minority
  if (surface + h >= CHUNK_HEIGHT) return;

  // Horizontal radii deliberately > height so form is a slab/mound not a plinth
  // Slight size headroom (~±2% range stretch via +1 on spans) — post-2.5 rock scale
  const radX = Math.max(h + 1, 4 + Math.floor(rand2(wx, wz, seed + 91) * (h + 5)));
  const radZ = Math.max(h + 1, 4 + Math.floor(rand2(wx + 3, wz - 2, seed + 92) * (h + 5)));
  // Asymmetry / stretch axis
  const stretchX = 0.85 + rand2(wx, wz, seed + 93) * 0.45;
  const stretchZ = 0.85 + rand2(wx - 1, wz + 4, seed + 94) * 0.45;

  /**
   * Playtest 2.8/2.13: no rocks in streams/ponds; no rocks on path lanes or
   * SITE_PAD footprints. PATH/SITE_PAD surface columns denied so spill can't
   * paint roads or pads.
   */
  const rockOkAt = (dx: number, dz: number): boolean => {
    const tx = wx + dx;
    const tz = wz + dz;
    const h = hydroAt(tx, tz, seed);
    if (h === 1 || h === 2) return false;
    // Paths + SITE_PAD footprints (2.13: pads stay clear of rocks)
    if (pathBlocksRockAt(tx, tz, seed)) return false;
    const llx = lx + dx;
    const llz = lz + dz;
    if (
      llx >= 0 &&
      llz >= 0 &&
      llx < CHUNK_SIZE &&
      llz < CHUNK_SIZE &&
      surface >= 0 &&
      surface < CHUNK_HEIGHT
    ) {
      const top = chunk.get(llx, surface, llz);
      if (top === BLOCK.PATH || top === BLOCK.SITE_PAD) return false;
    }
    return true;
  };

  for (let dy = 1; dy <= h; dy++) {
    const t = dy / h;
    // Flatter dome: stays wide until near top, then rounds off
    const profile = Math.pow(Math.max(0.02, 1 - t * t * 0.95), 0.55);
    const rx = Math.max(1, Math.floor(radX * profile * stretchX));
    const rz = Math.max(1, Math.floor(radZ * profile * stretchZ));
    for (let dx = -rx - 1; dx <= rx + 1; dx++) {
      for (let dz = -rz - 1; dz <= rz + 1; dz++) {
        if (!rockOkAt(dx, dz)) continue;
        const nx = dx / (rx + 0.35);
        const nz = dz / (rz + 0.35);
        // Elliptical base + noise nibble for rough edges
        const ell = nx * nx + nz * nz;
        if (ell > 1.05) continue;
        const rough = rand2(wx + dx, wz + dz + dy * 5, seed + 99);
        if (ell > 0.55 && rough > 0.72) continue; // chip outer rim
        if (ell > 0.85 && rough > 0.4) continue;
        // Occasional missing blocks for roughness
        if (rough > 0.94 && t > 0.15) continue;
        // Occasional side knobs near base
        if (ell > 1.0 && t < 0.35 && rough < 0.18) {
          trySet(chunk, lx + dx, surface + dy, lz + dz, BLOCK.ROCK);
          continue;
        }
        trySet(chunk, lx + dx, surface + dy, lz + dz, BLOCK.ROCK);
      }
    }
  }

  // Small side bulges / stacked ledge for non-plinth silhouette
  const bulges = 1 + Math.floor(rand2(wx, wz, seed + 100) * 3);
  for (let i = 0; i < bulges; i++) {
    const ang = rand2(wx + i, wz - i, seed + 101);
    const bx = Math.floor(Math.cos(ang * Math.PI * 2) * (radX * 0.65));
    const bz = Math.floor(Math.sin(ang * Math.PI * 2) * (radZ * 0.65));
    const bh = 1 + Math.floor(rand2(wx + bx, wz + bz, seed + 102) * Math.min(4, h));
    const br = 1 + Math.floor(rand2(wx - bx, wz + bz, seed + 103) * 2);
    for (let dy = 1; dy <= bh; dy++) {
      for (let dx = -br; dx <= br; dx++) {
        for (let dz = -br; dz <= br; dz++) {
          if (dx * dx + dz * dz > br * br + 0.5) continue;
          if (!rockOkAt(bx + dx, bz + dz)) continue;
          if (rand2(wx + dx + bx, wz + dz + bz + dy, seed + 104) > 0.85) continue;
          trySet(chunk, lx + bx + dx, surface + dy, lz + bz + dz, BLOCK.ROCK);
        }
      }
    }
  }
}

/**
 * Tall grass column height — full rescale: −20% density at gates + more bare/
 * short patches with remaining grass clumped (short majority, rare medium clumps).
 * Distribution: ~12% bare (caller), ~55% height 1–2, ~28% 3–5, ~5% clump 6–10.
 */
function placeTallGrass(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
  wx: number,
  wz: number,
  seed: number,
): void {
  const u = rand2(wx, wz, seed + 12);
  // Clump field: nearby cells share a "long grass patch" bias
  const clump = fbm2D(wx * 0.09 + 2, wz * 0.09 - 4, seed + 13, 2, 2, 0.5);
  // Bare patches inside planted cells — sparse ground between clumps
  if (clump < 0.32 && u > 0.55) return;
  let h: number;
  if (clump > 0.72 && u < 0.4) {
    // Medium clump (was 10–20 — shortened with land band)
    h = 6 + Math.floor(rand2(wx, wz, seed + 14) * 5); // 6–10
  } else if (u < 0.6) {
    h = 1 + Math.floor(rand2(wx, wz, seed + 15) * 2); // 1–2 majority short
  } else if (u < 0.92) {
    h = 3 + Math.floor(rand2(wx, wz, seed + 16) * 3); // 3–5 medium
  } else {
    h = 5 + Math.floor(rand2(wx, wz, seed + 17) * 4); // 5–8 sparse tall
  }
  h = Math.max(1, Math.min(10, h));
  if (surface + h >= CHUNK_HEIGHT) return;
  for (let dy = 1; dy <= h; dy++) {
    trySet(chunk, lx, surface + dy, lz, BLOCK.TALL_GRASS);
  }
}

/**
 * Path/road cell — paints PATH (warm tan packed earth) over biome / site pad
 * on the existing landform height (no flatten).
 * Never places on pond beds, stream beds, water, or empty/sea columns.
 * Playtest 2.7: roads win over every non-hydro ground type.
 * Playtest 2.16: also wins over SITE_PAD on re-assert (PATH priority pass).
 * Playtest 2.17: streams keep STREAM_BED — PATH no longer overpaints channels
 * (travel-way precedence: stream bed + road lanes both protected).
 */
function placePathCell(
  chunk: Chunk,
  lx: number,
  surface: number,
  lz: number,
): void {
  const top = chunk.get(lx, surface, lz);
  // Roads never cross ponds, streams, bridges, or aqua sand beaches; skip empty/sea / path
  if (
    top === BLOCK.POND_BED ||
    top === BLOCK.STREAM_BED ||
    top === BLOCK.BRIDGE ||
    top === BLOCK.WATER ||
    top === BLOCK.SAND ||
    top === BLOCK.AIR ||
    top === BLOCK.PATH
  ) {
    return;
  }
  // Clear tall-grass / stray props sitting on the path (grass is 1–20 high)
  for (let y = surface + 1; y < CHUNK_HEIGHT; y++) {
    const above = chunk.get(lx, y, lz);
    if (above === BLOCK.AIR) break;
    if (
      above === BLOCK.TALL_GRASS ||
      above === BLOCK.LEAVES ||
      above === BLOCK.PINE_LEAVES ||
      above === BLOCK.WILLOW_LEAVES ||
      above === BLOCK.LOG ||
      above === BLOCK.PINE_LOG ||
      above === BLOCK.WILLOW_LOG ||
      above === BLOCK.ROCK
    ) {
      chunk.set(lx, y, lz, BLOCK.AIR);
      continue;
    }
    break;
  }
  chunk.set(lx, surface, lz, BLOCK.PATH);
}

/**
 * Clear props above a deck Y so bridge sheets stay walkable.
 */
function clearAboveDeck(
  chunk: Chunk,
  lx: number,
  deckY: number,
  lz: number,
): void {
  for (let y = deckY + 1; y < CHUNK_HEIGHT; y++) {
    const above = chunk.get(lx, y, lz);
    if (above === BLOCK.AIR) break;
    if (
      above === BLOCK.TALL_GRASS ||
      above === BLOCK.LEAVES ||
      above === BLOCK.PINE_LEAVES ||
      above === BLOCK.WILLOW_LEAVES ||
      above === BLOCK.LOG ||
      above === BLOCK.PINE_LOG ||
      above === BLOCK.WILLOW_LOG ||
      above === BLOCK.ROCK ||
      above === BLOCK.WATER
    ) {
      chunk.set(lx, y, lz, BLOCK.AIR);
      continue;
    }
    break;
  }
}

/**
 * Flat BRIDGE deck over a stream column at road×stream crossings (playtest 2.21).
 * Deck sits at max(approach bank surface, stream bed + min clearance) so it
 * matches the road approach height rather than diving into the channel.
 * Never paints over ponds. Keeps STREAM_BED below for visual bed under deck.
 */
function placeBridgeCell(
  chunk: Chunk,
  lx: number,
  lz: number,
  bedY: number,
  deckY: number,
): void {
  const y = Math.max(
    bedY + 2,
    Math.min(CHUNK_HEIGHT - 2, Math.floor(deckY)),
  );
  // Don't bury the deck under sea-static water without clearing a path up
  clearAboveDeck(chunk, lx, y, lz);
  // If bed is under sea, clear water at/above deck so sheet is visible
  if (chunk.get(lx, y, lz) === BLOCK.WATER) {
    chunk.set(lx, y, lz, BLOCK.AIR);
  }
  chunk.set(lx, y, lz, BLOCK.BRIDGE);
  // Ensure one air above for headroom (walkable)
  if (y + 1 < CHUNK_HEIGHT && chunk.get(lx, y + 1, lz) === BLOCK.WATER) {
    chunk.set(lx, y + 1, lz, BLOCK.AIR);
  }
}

/**
 * Path lane at world xz (playtest 2.6).
 * 0 = none, 1 = footpath, 2 = road core (travel strip), 3 = road shoulder.
 * Core stays clear for walking; shoulders may host roadside rocks.
 * Roads still paint PATH across core+shoulder (~50 wide).
 */
export type PathLane = 0 | 1 | 2 | 3;

/** Playtest 2.12+: invisible path buffer from stream bank top (voxels). */
export const STREAM_PATH_BUFFER_MIN = 10;
export const STREAM_PATH_BUFFER_MAX = 20;
/** Legal road×stream incidence: 45°–90° (dot of unit tangents ≤ cos45). */
const CROSS_MAX_ABS_DOT = Math.SQRT1_2; // cos 45°
/** Parallel-kill threshold — slightly looser than 45° so shallow grazes die too (~40°). */
const STRADDLE_KILL_ABS_DOT = Math.cos((40 * Math.PI) / 180);
/** Building/ruin site keepouts (from stream-buffer edge + pond edge). */
export const SITE_STREAM_KEEPOUT = 100;
export const SITE_POND_KEEPOUT = 100;
/** No SITE_PAD centers inside this radius of origin (playtest 2.16). */
export const SITE_ORIGIN_BAN = 1000;
/**
 * Beside-road ring (blocks from pad center to nearest road sample).
 * Pads sit beside the way — not on the travel strip (playtest 2.16).
 */
export const SITE_ROAD_CLEAR_MIN = 28;
export const SITE_ROAD_NEAR_MAX = 150;
/** Approx major axis of oblong/kidney site pads (voxels). */
export const SITE_MAJOR_SPAN = 300;

export type StreamGeom = {
  /** True if column is inside the stream channel (half-width). */
  inChannel: boolean;
  /** Distance from stream centerline (across). */
  centerDist: number;
  /** Local stream half-width at this sample. */
  halfW: number;
  /** Stream bearing radians in XZ (tangent along flow). */
  angle: number;
  /** Lateral unit (pointing +across). */
  nx: number;
  nz: number;
  /**
   * 0.2.52 flow direction unit along the channel (+cos/sin of angle).
   * Downstream sense is convention-only (bearing, not gravity); useful for
   * foam/debris/path incidence. Same magnitude as (cos θ, sin θ).
   */
  flowDirX: number;
  flowDirZ: number;
};

/**
 * Nearest artery/swamp stream geometry at (wx,wz) for path buffer/angle bias.
 * Mirrors arteryStreamAt / swampRidgeStreamAt centerline math (read-only).
 * Playtest 2.21: shared STREAM_ARTERIES + multi-join halfW (+20% base).
 * 0.2.52: shared parallel-merge filter + flowDir on StreamGeom.
 */
export function nearestStreamGeom(
  wx: number,
  wz: number,
  seed: number,
): StreamGeom | null {
  // Match arteryStreamAt sample warp so buffer hugs real beds.
  const { sx, sz } = rockGardenRepulsion(wx, wz, seed);

  let best: StreamGeom | null = null;
  let bestScore = Infinity; // centerDist - halfW (negative = inside)

  // Swamp ridge streams (playtest 2.10) — approximate local bearing via dual ridges.
  if (biomeAt(wx, wz, seed) === BIOME.SWAMP) {
    const a1 = fbm2D(sx * 0.0055 + 3, sz * 0.0055 - 7, seed + 890, 4, 2.1, 0.52);
    const a2 = fbm2D(sx * 0.0055 - 11, sz * 0.0055 + 17, seed + 895, 4, 2.1, 0.52);
    const b = fbm2D(sx * 0.01 - 14, sz * 0.01 + 5, seed + 891, 3, 2, 0.5);
    const r1 = Math.abs(a1 - 0.5);
    const r2 = Math.abs(a2 - 0.5);
    const ridge = Math.min(r1, r2);
    const useR1 = r1 <= r2;
    // Gradient of the active ridge field ≈ local across-axis
    const eps = 1.5;
    const sampleA = (x: number, z: number) =>
      useR1
        ? fbm2D(x * 0.0055 + 3, z * 0.0055 - 7, seed + 890, 4, 2.1, 0.52)
        : fbm2D(x * 0.0055 - 11, z * 0.0055 + 17, seed + 895, 4, 2.1, 0.52);
    const gx = sampleA(sx + eps, sz) - sampleA(sx - eps, sz);
    const gz = sampleA(sx, sz + eps) - sampleA(sx, sz - eps);
    const gLen = Math.hypot(gx, gz) || 1;
    // Normal points across channel; tangent is perpendicular
    const nx = gx / gLen;
    const nz = gz / gLen;
    const angle = Math.atan2(-nx, nz); // tangent along flow
    const mouth = nearPondBasin(wx, wz, seed, 48);
    const mainW = mouth ? 0.048 : 0.04;
    // Map ridge field distance into voxel-ish half-width (~ bank width); +20% vs 2.15
    const halfW = 10.8 * (mouth ? 1.2 : 1);
    const centerDist = (ridge / Math.max(1e-6, mainW)) * halfW;
    const score = centerDist - halfW;
    if (score < bestScore) {
      bestScore = score;
      best = {
        inChannel: swampRidgeStreamAt(wx, wz, seed) === 1,
        centerDist,
        halfW,
        angle,
        nx,
        nz,
        flowDirX: Math.cos(angle),
        flowDirZ: Math.sin(angle),
      };
    }
    void b;
  }

  const mouth = nearPondBasin(wx, wz, seed, 48);
  const rawHits = collectStreamArteryHits(sx, sz, seed);
  if (rawHits.length > 0) {
    // Same T-merge + parallel filter as arteryStreamAt (lockstep)
    const halfW0 = streamHalfWidth0(sx, sz, seed, mouth);
    const hits = filterStreamArteryHits(rawHits, halfW0);

    if (hits.length > 0) {
      const halfW = streamHalfWidthAt(sx, sz, seed, mouth, hits);
      let bestHit: StreamArteryHit | null = null;
      for (const h of hits) {
        if (!bestHit || h.dist < bestHit.dist) bestHit = h;
      }
      if (bestHit) {
        const score = bestHit.dist - halfW;
        // Last consumer of bestScore — assign best only (no further compares).
        if (score < bestScore) {
          best = {
            inChannel: bestHit.dist <= halfW,
            centerDist: bestHit.dist,
            halfW,
            angle: bestHit.angle,
            nx: -bestHit.s,
            nz: bestHit.c,
            flowDirX: bestHit.c,
            flowDirZ: bestHit.s,
          };
        }
      }
    }
  }

  return best;
}

export function streamPathBufferDepth(
  wx: number,
  wz: number,
  seed: number,
): number {
  const g = nearestStreamGeom(wx, wz, seed);
  if (!g) return -1e9;
  // Distance past bank top toward land (0 at bank top, positive on land)
  return g.centerDist - g.halfW;
}

/** True when (wx,wz) is in the invisible 10–20 buffer ring outside the bank. */
export function streamInPathBuffer(
  wx: number,
  wz: number,
  seed: number,
): boolean {
  const g = nearestStreamGeom(wx, wz, seed);
  if (!g) return false;
  const bufN = fbm2D(wx * 0.01, wz * 0.01, seed + 912, 2, 2, 0.5);
  const buf =
    STREAM_PATH_BUFFER_MIN +
    (STREAM_PATH_BUFFER_MAX - STREAM_PATH_BUFFER_MIN) * bufN;
  const pastBank = g.centerDist - g.halfW;
  return pastBank >= 0 && pastBank <= buf;
}

/**
 * Playtest 2.12/2.16 building/ruin placeholder pads — oblong/kidney ~300 across.
 * 2.16 rules:
 * - no pad centers within SITE_ORIGIN_BAN of (0,0)
 * - pads sit *beside* roads (clearance ring), never straddling the travel strip
 * - raised road clearance + near-road max so pads hug the way without roads through them
 * - keepout 100 from stream-buffer edge and ponds
 */
export function sitePadAt(wx: number, wz: number, seed: number): boolean {
  // Coarse cell grid so pads are sparse and stable
  const cell = 520;
  const cx = Math.floor(wx / cell);
  const cz = Math.floor(wz / cell);
  // Jittered center inside cell
  const jx = (rand2(cx, cz, seed + 9401) - 0.5) * cell * 0.55;
  const jz = (rand2(cx, cz, seed + 9402) - 0.5) * cell * 0.55;
  const sx = cx * cell + cell * 0.5 + jx;
  const sz = cz * cell + cell * 0.5 + jz;

  // Hard ban: no site centers inside origin keepout (playtest 2.16)
  if (Math.hypot(sx, sz) < SITE_ORIGIN_BAN) return false;

  // Presence gate — sparse but not rare (lower = more pads)
  if (rand2(cx, cz, seed + 9403) < 0.48) return false;

  // Beside-road ring: find nearest road sample distance from pad center.
  // Reject if road runs through the pad (too close) or pad is too far from a way.
  let nearestRoad = Infinity;
  for (const [dx, dz] of [
    [0, 0],
    [28, 0],
    [-28, 0],
    [0, 28],
    [0, -28],
    [40, 0],
    [-40, 0],
    [0, 40],
    [0, -40],
    [56, 20],
    [-56, -20],
    [80, 30],
    [-70, -40],
    [100, 0],
    [-100, 0],
    [0, 100],
    [0, -100],
    [120, 40],
    [-120, -40],
    [90, 90],
    [-90, 90],
    [140, 0],
    [-140, 0],
    [0, 140],
    [0, -140],
    [160, 40],
    [-150, -50],
  ] as const) {
    const lane = pathLaneAtRaw(sx + dx, sz + dz, seed, /*skipStreamBias*/ true);
    if (lane === 1 || lane === 2 || lane === 3) {
      const d = Math.hypot(dx, dz);
      if (d < nearestRoad) nearestRoad = d;
    }
  }
  if (nearestRoad === Infinity) return false;
  // Must be beside the road, not on it / not with road through pad footprint
  if (nearestRoad < SITE_ROAD_CLEAR_MIN) return false;
  if (nearestRoad > SITE_ROAD_NEAR_MAX) return false;

  // Keepout: ponds
  if (nearPondBasin(sx, sz, seed, SITE_POND_KEEPOUT)) return false;
  // Keepout: stream buffer edge + 100
  {
    const g = nearestStreamGeom(sx, sz, seed);
    if (g) {
      const bufN = fbm2D(sx * 0.01, sz * 0.01, seed + 912, 2, 2, 0.5);
      const buf =
        STREAM_PATH_BUFFER_MIN +
        (STREAM_PATH_BUFFER_MAX - STREAM_PATH_BUFFER_MIN) * bufN;
      // distance outside buffer edge = centerDist - halfW - buf
      const outsideBuf = g.centerDist - g.halfW - buf;
      if (outsideBuf < SITE_STREAM_KEEPOUT) return false;
    }
  }

  // Oblong / kidney footprint ~300 major axis
  const ang = rand2(cx, cz, seed + 9404) * Math.PI * 2;
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  const dx = wx - sx;
  const dz = wz - sz;
  const localX = dx * c + dz * s;
  const localZ = -dx * s + dz * c;
  // major axis full span ≈ 300 → half ~138–162
  const halfMajor =
    SITE_MAJOR_SPAN * 0.5 * (0.92 + 0.16 * rand2(cx, cz, seed + 9405));
  const halfMinor = halfMajor * (0.42 + 0.2 * rand2(cx, cz, seed + 9406));
  // Kidney: indent one side with a secondary lobe cut
  const nx = localX / halfMajor;
  const nz = localZ / halfMinor;
  let d = nx * nx + nz * nz;
  const kidney =
    0.18 *
    Math.exp(-((localX / halfMajor - 0.35) ** 2) * 4) *
    Math.exp(-((localZ / halfMinor) ** 2) * 2) *
    (rand2(cx, cz, seed + 9407) > 0.35 ? 1 : 0);
  d = d + kidney;
  return d <= 1.0;
}

/**
 * Path lane without stream bias — used by sitePad near-road probes to avoid
 * recursion with stream helpers that only need coarse road presence.
 */
function pathLaneAtRaw(
  wx: number,
  wz: number,
  seed: number,
  _skipStreamBias: boolean,
): PathLane {
  return pathLaneAtCore(wx, wz, seed, false);
}

/**
 * World-space path field — elongated arteries with a dominant bearing + light meander.
 * Main roads target ~50 voxels wide (±10% → half-width 22.5–27.5). No splotchy
 * ridge blobs (playtest 2.5).
 *
 * Playtest 2.6: gentle lateral bend away from rock-mass so arteries curve around
 * outcrops; core vs shoulder so rocks can sit on the verge, never the centerline.
 *
 * Cartesian world: X east, Z south, Y up; origin (0,0) = spawn / future village.
 */
function pathLaneAtCore(
  wx: number,
  wz: number,
  seed: number,
  applyStreamBias: boolean,
): PathLane {
  /**
   * Playtest 2.15 road rewrite (scrap prior presence-gated arteries):
   * - Continuous long arteries (no random presence dropouts / random stops)
   * - Stronger slow meander so roads wind instead of ruler-straight
   * - No rock-mass lateral kill — PATH overpaints tan/rock (placePathCell)
   * - Keep hard anti-straddle vs streams + ~45–90° bridge snaps
   * - Sparse footpaths remain low-density separate field
   */
  const arteries: ReadonlyArray<{
    seedOff: number;
    baseAngle: number;
    /** Across-axis center; 0 = through origin. */
    centerOff: number;
    /** If true, always continuous near hub (and everywhere — continuous artery). */
    throughHub?: boolean;
    hubRadius?: number;
  }> = [
    // Continuous trunk through the village — always on
    {
      seedOff: 701,
      baseAngle: 0.22,
      centerOff: 0,
      throughHub: true,
      hubRadius: 360,
    },
    // Continuous offset arteries — no presence gating (2.15 continuity)
    { seedOff: 711, baseAngle: 1.52, centerOff: 300 },
    { seedOff: 721, baseAngle: 0.88, centerOff: -340 },
    { seedOff: 741, baseAngle: 2.05, centerOff: 520 },
  ];

  let bestLane: PathLane = 0;
  let bestDist = Infinity;
  const hubDist = Math.hypot(wx, wz);

  for (const art of arteries) {
    // Slow bearing drift — coherent but winds more than 2.14
    const angN = fbm2D(
      wx * 0.00028 + art.seedOff * 0.01,
      wz * 0.00028 - art.seedOff * 0.007,
      seed + art.seedOff,
      3,
      2,
      0.5,
    );
    const angle = art.baseAngle + (angN - 0.5) * 0.42;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const along = wx * c + wz * s;
    const across = -wx * s + wz * c;

    // Stronger multi-scale meander (tens of blocks) — wind, not scribble
    const meander =
      Math.sin(along * 0.0048 + art.seedOff * 0.017) * 28 +
      Math.sin(along * 0.011 + art.seedOff * 0.09) * 14 +
      (fbm2D(along * 0.0028, art.seedOff * 0.11, seed + art.seedOff + 5, 3, 2, 0.5) -
        0.5) *
        18;

    // Width ~48 ±12% → half-width ~21–27 (full width ~42–54)
    const wN = fbm2D(along * 0.0024, art.seedOff * 0.17, seed + art.seedOff + 9, 2, 2, 0.5);
    const halfW = 24 * (0.88 + 0.24 * wN);

    // 2.15: arteries are continuous — no presence fbm dropout (that caused random stops)
    const forceHub =
      art.throughHub === true && hubDist <= (art.hubRadius ?? 200);

    // Unbiased distance to this artery centerline (pre stream warp)
    const rawDist = Math.abs(across - art.centerOff - meander);

    // Keep hard anti-straddle / clean bridge crosses (2.13+)
    let dist = rawDist;
    let skipArtery = false;
    if (applyStreamBias) {
      const sg = nearestStreamGeom(wx, wz, seed);
      if (sg) {
        const bufN = fbm2D(wx * 0.01, wz * 0.01, seed + 912, 2, 2, 0.5);
        const buf =
          STREAM_PATH_BUFFER_MIN +
          (STREAM_PATH_BUFFER_MAX - STREAM_PATH_BUFFER_MIN) * bufN;
        const pastBank = sg.centerDist - sg.halfW;
        const straddleBand = sg.halfW + buf + halfW + 24;
        const influence = buf + sg.halfW * 2.5;

        let dAng = angle - sg.angle;
        dAng = ((dAng + Math.PI) % (Math.PI * 2)) - Math.PI;
        if (dAng > Math.PI / 2) dAng -= Math.PI;
        if (dAng < -Math.PI / 2) dAng += Math.PI;
        const absDot = Math.abs(Math.cos(dAng));
        const sinInc = Math.sqrt(Math.max(0, 1 - absDot * absDot));

        const arteryNear = rawDist <= halfW * 2.4;
        const inCorridor = sg.centerDist < straddleBand;
        const uAx = -s;
        const uAz = c;
        const footSigned = across - art.centerOff - meander;
        const footX = wx - footSigned * uAx;
        const footZ = wz - footSigned * uAz;
        const footG = nearestStreamGeom(footX, footZ, seed);
        const footPast = footG ? footG.centerDist - footG.halfW : Infinity;
        const epsA = Math.max(
          20,
          Math.min(64, (sg.halfW * 2.4 + buf * 0.9) / Math.max(0.22, sinInc)),
        );
        const fFwd = nearestStreamGeom(footX + c * epsA, footZ + s * epsA, seed);
        const fBak = nearestStreamGeom(footX - c * epsA, footZ - s * epsA, seed);
        const pastFwd = fFwd ? fFwd.centerDist - fFwd.halfW : footPast;
        const pastBak = fBak ? fBak.centerDist - fBak.halfW : footPast;
        const crossSpeed = Math.abs(pastFwd - pastBak) / (2 * epsA);
        const localMin =
          footPast <= pastFwd + 3 && footPast <= pastBak + 3;
        const bankClear = Math.max(6, buf * 0.4);
        const arteryCrosses =
          footG !== null &&
          localMin &&
          footPast < footG.halfW * 0.45 &&
          pastFwd > bankClear &&
          pastBak > bankClear &&
          rawDist <= halfW * 1.05 &&
          crossSpeed >= 0.42 &&
          absDot <= 0.88 &&
          sinInc >= 0.35;

        if (arteryNear && inCorridor && absDot > CROSS_MAX_ABS_DOT) {
          if (arteryCrosses && footG) {
            const fnx = footG.nx;
            const fnz = footG.nz;
            const gPlus = nearestStreamGeom(
              footX + fnx * 2.5,
              footZ + fnz * 2.5,
              seed,
            );
            const gMinus = nearestStreamGeom(
              footX - fnx * 2.5,
              footZ - fnz * 2.5,
              seed,
            );
            const towardCenter =
              gPlus && gMinus && gPlus.centerDist < gMinus.centerDist ? 1 : -1;
            const crossX = footX + towardCenter * fnx * footG.centerDist;
            const crossZ = footZ + towardCenter * fnz * footG.centerDist;
            const sa = Math.cos(footG.angle);
            const sb = Math.sin(footG.angle);
            dist = Math.abs((wx - crossX) * sa + (wz - crossZ) * sb);
            const bridgeHalf = halfW * (forceHub ? 0.95 : 0.88);
            if (dist <= bridgeHalf && dist < bestDist) {
              bestDist = dist;
              const coreHalf = bridgeHalf * 0.62;
              bestLane = dist <= coreHalf ? 2 : 3;
            }
            continue;
          }
          // Bank-hug / stream-follow — hard-kill; hub soft-curve only mild shallow.
          if (forceHub && absDot <= STRADDLE_KILL_ABS_DOT) {
            const tt = Math.max(
              0,
              Math.min(1, 1 - pastBank / Math.max(1, influence)),
            );
            const latSign = dAng >= 0 ? 1 : -1;
            const meanderUse =
              meander + latSign * (absDot - CROSS_MAX_ABS_DOT) * 22 * tt * tt;
            const target = sg.angle + Math.PI / 2;
            let ad = target - angle;
            ad = ((ad + Math.PI) % (Math.PI * 2)) - Math.PI;
            const blend = Math.min(0.5, 0.4 * tt * tt);
            const newAng = angle + ad * blend;
            const nc = Math.cos(newAng);
            const ns = Math.sin(newAng);
            const acrossUse = -wx * ns + wz * nc;
            dist = Math.abs(acrossUse - art.centerOff - meanderUse);
          } else {
            skipArtery = true;
          }
        } else if (
          arteryNear &&
          pastBank >= 0 &&
          pastBank <= buf &&
          absDot <= CROSS_MAX_ABS_DOT
        ) {
          const tb = 1 - pastBank / Math.max(1, buf);
          const meanderUse =
            meander +
            (fbm2D(wx * 0.02, wz * 0.02, seed + 913, 2, 2, 0.5) - 0.5) *
              6 *
              tb;
          dist = Math.abs(across - art.centerOff - meanderUse);
        } else if (
          arteryNear &&
          inCorridor &&
          absDot <= CROSS_MAX_ABS_DOT &&
          pastBank < 0
        ) {
          dist = rawDist;
        }
      }
    }

    // Playtest 2.20: continuous detour around sand beaches + ponds.
    // Warp the artery centerline laterally (envelope / clear-offset search)
    // so roads plot around obstacles instead of kill/gap through them.
    // Skip inside stream corridors — stream anti-straddle / bridges own that space;
    // detour warping there inflates bank-hug PCA and fights 45–90° crosses.
    if (applyStreamBias && !skipArtery) {
      const uAx = -s;
      const uAz = c;
      const footSigned = across - art.centerOff - meander;
      const nearStream =
        (() => {
          const sg = nearestStreamGeom(wx, wz, seed);
          if (!sg) return false;
          const bufN = fbm2D(wx * 0.01, wz * 0.01, seed + 912, 2, 2, 0.5);
          const buf =
            STREAM_PATH_BUFFER_MIN +
            (STREAM_PATH_BUFFER_MAX - STREAM_PATH_BUFFER_MIN) * bufN;
          return sg.centerDist < sg.halfW + buf + halfW + 28;
        })();
      if (!nearStream && Math.abs(footSigned) <= halfW + DETOUR_SEARCH_MAX) {
        const footX = wx - footSigned * uAx;
        const footZ = wz - footSigned * uAz;
        const preferN = fbm2D(
          along * 0.0011 + art.seedOff * 0.03,
          art.seedOff * 0.19,
          seed + art.seedOff + 940,
          2,
          2,
          0.5,
        );
        const preferSign = preferN >= 0.5 ? 1 : -1;
        const detour = arteryObstacleDetour(
          footX,
          footZ,
          uAx,
          uAz,
          c,
          s,
          halfW,
          seed,
          preferSign,
        );
        if (!Number.isFinite(detour)) {
          // No clear beltway within search — drop this artery (last resort)
          skipArtery = true;
        } else {
          // Only rewrite lateral distance when the centerline actually warps;
          // keep stream-bias dist when detour is ~0 so soft stream curves survive.
          if (Math.abs(detour) > 0.5) {
            dist = Math.abs(footSigned - detour);
          }
          // Never claim PATH on sand/pond even if math rounds onto a fringe cell
          if (pathBlockedAt(wx, wz, seed)) {
            skipArtery = true;
          }
        }
      } else if (pathBlockedAt(wx, wz, seed)) {
        // Still hard-kill PATH on sand/pond even near streams / far from foot
        skipArtery = true;
      }
    }

    if (skipArtery) continue;

    if (dist <= halfW && dist < bestDist) {
      bestDist = dist;
      const coreHalf = halfW * 0.62;
      bestLane = dist <= coreHalf ? 2 : 3;
    }
  }

  if (bestLane !== 0) return bestLane;

  // Sparse thin footpaths (~10–14 wide) — separate low-density field, not road scraps
  const trailRegion = fbm2D(wx * 0.0018 + 61, wz * 0.0018 - 29, seed + 730, 3, 2, 0.5);
  if (trailRegion < 0.52) return 0;
  const tAngN = fbm2D(wx * 0.0005, wz * 0.0005, seed + 731, 2, 2, 0.5);
  const tAngle = 0.7 + (tAngN - 0.5) * 0.5;
  const tc = Math.cos(tAngle);
  const ts = Math.sin(tAngle);
  const tAlong = wx * tc + wz * ts;
  const tAcross = -wx * ts + wz * tc;
  const tMeander =
    Math.sin(tAlong * 0.01) * 10 +
    (fbm2D(tAlong * 0.006, 3.2, seed + 732, 2, 2, 0.5) - 0.5) * 8;
  const tCenter =
    (fbm2D(0.4, 0.9, seed + 733, 2, 2, 0.5) - 0.5) * 700;
  const tDist = Math.abs(tAcross - tCenter - tMeander);
  const tHalf = 6 * (0.9 + 0.25 * fbm2D(tAlong * 0.004, 1.1, seed + 734, 2, 2, 0.5));
  const tPresence = fbm2D(tAlong * 0.0015, 2.2, seed + 735, 3, 2, 0.5);
  if (tPresence > 0.56 && tDist <= tHalf) {
    if (applyStreamBias) {
      // No footpaths on aqua sand beaches or pond basins
      if (pathBlockedAt(wx, wz, seed)) return 0;
      const sg = nearestStreamGeom(wx, wz, seed);
      if (sg) {
        const bufN = fbm2D(wx * 0.01, wz * 0.01, seed + 912, 2, 2, 0.5);
        const buf =
          STREAM_PATH_BUFFER_MIN +
          (STREAM_PATH_BUFFER_MAX - STREAM_PATH_BUFFER_MIN) * bufN;
        if (sg.centerDist < sg.halfW + buf + tHalf + 48) {
          let dAng = tAngle - sg.angle;
          dAng = ((dAng + Math.PI) % (Math.PI * 2)) - Math.PI;
          if (dAng > Math.PI / 2) dAng -= Math.PI;
          if (dAng < -Math.PI / 2) dAng += Math.PI;
          if (Math.abs(Math.cos(dAng)) > STRADDLE_KILL_ABS_DOT) return 0;
        }
      }
    }
    return 1;
  }

  return 0;
}

/** Path/road presence — 2.13 hard anti-straddle + ~45–90° cross snap. */
export function pathLaneAt(wx: number, wz: number, seed: number): PathLane {
  return pathLaneAtCore(wx, wz, seed, true);
}


/**
 * True when rocks must not occupy this column.
 * Playtest 2.8: block footpath + road core + entire shoulder/path-edge
 * (lane 1/2/3). Playtest 2.13: also clear SITE_PAD footprints.
 */
function pathBlocksRockAt(wx: number, wz: number, seed: number): boolean {
  const lane = pathLaneAt(wx, wz, seed);
  if (lane === 1 || lane === 2 || lane === 3) return true;
  return sitePadAt(wx, wz, seed);
}

function isPlantableSurface(id: number): boolean {
  return (
    id === BLOCK.GRASS ||
    id === BLOCK.FOREST_GRASS ||
    id === BLOCK.SWAMP_GRASS ||
    id === BLOCK.DIRT ||
    id === BLOCK.SAND
    // never plant on PATH / BRIDGE / SITE_PAD / beds / water
  );
}

/**
 * Fill a chunk from deterministic worldgen.
 * Y-up; surface varies for noise mode; scatter on noise only. No caves.
 * Playtest 2.7: expanded land/hydro neighborhood grid + column caches so
 * surfaceY/path/biome aren't re-noised O(R²) per cell (move-hitch under view=20).
 */
export function generateChunk(
  cx: number,
  cz: number,
  opts: WorldgenOptions,
): Chunk {
  const chunk = new Chunk(cx, cz);
  const ox = cx * CHUNK_SIZE;
  const oz = cz * CHUNK_SIZE;
  const flatH = opts.flatHeight ?? SEA_LEVEL;
  const seed = opts.seed;
  const nCells = CHUNK_SIZE * CHUNK_SIZE;
  // Column caches — filled pass 1, reused pass 2/3 (noise mode only values matter)
  const surfY = new Int16Array(nCells);
  const landY = new Int16Array(nCells);
  const hydroCol = new Uint8Array(nCells);
  const biomeCol = new Uint8Array(nCells);
  const laneCol = new Uint8Array(nCells);

  // Neighborhood pad for surfaceY bank/thalweg — must cover max soft-bank fade (~11)
  // so edge columns match interior (no cliff seams at chunk borders). 0.2.53: was 9.
  const PAD = 12;
  const gW = CHUNK_SIZE + PAD * 2;
  const gN = gW * gW;
  const landGrid = new Int16Array(gN);
  const hydroGrid = new Uint8Array(gN);
  // Core-only biome cache (chunk footprint) — filled with land/hydro pad pass
  const biomeCore = new Uint8Array(nCells);
  const grids =
    opts.mode === "noise"
      ? {
          landAt: (x: number, z: number): number => {
            const gx = x - ox + PAD;
            const gz = z - oz + PAD;
            return landGrid[gz * gW + gx]!;
          },
          hydroAt: (x: number, z: number): HydroClass => {
            const gx = x - ox + PAD;
            const gz = z - oz + PAD;
            return hydroGrid[gz * gW + gx]! as HydroClass;
          },
        }
      : undefined;

  if (opts.mode === "noise" && grids) {
    for (let gz = 0; gz < gW; gz++) {
      for (let gx = 0; gx < gW; gx++) {
        const wx = ox + gx - PAD;
        const wz = oz + gz - PAD;
        const gi = gz * gW + gx;
        const biome = biomeAt(wx, wz, seed);
        const land = noiseSurfaceY(wx, wz, seed, biome);
        landGrid[gi] = land;
        hydroGrid[gi] = land >= SEA_LEVEL ? hydroAt(wx, wz, seed) : 0;
        // Core cells only
        if (
          gx >= PAD &&
          gz >= PAD &&
          gx < PAD + CHUNK_SIZE &&
          gz < PAD + CHUNK_SIZE
        ) {
          biomeCore[(gz - PAD) * CHUNK_SIZE + (gx - PAD)] = biome;
        }
      }
    }
  }

  // Pass 1: columns (stone/dirt/surface/water) + stream/pond bed paint
  for (let lz = 0; lz < CHUNK_SIZE; lz++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = ox + lx;
      const wz = oz + lz;
      const ci = lz * CHUNK_SIZE + lx;

      let surface: number;
      let biome: BiomeId = BIOME.GRASSLANDS;
      let land = flatH;
      let hydro: HydroClass = 0;
      if (opts.mode === "flat") {
        surface = flatH;
      } else {
        // Includes hydro bank depression so beds sit below surrounding land
        land = grids!.landAt(wx, wz);
        biome = biomeCore[ci]! as BiomeId;
        hydro = grids!.hydroAt(wx, wz);
        surface = surfaceY(wx, wz, seed, land, grids);
      }
      surfY[ci] = surface;
      landY[ci] = land;
      hydroCol[ci] = hydro;
      biomeCol[ci] = biome;

      for (let ly = 0; ly < CHUNK_HEIGHT; ly++) {
        let id: number = BLOCK.AIR;
        // Band law: y0 always bedrock (indestructible floor)
        if (ly === 0) {
          id = BLOCK.BEDROCK;
        } else if (ly < surface) {
          id = fillUnderSurface(ly, surface, biome);
        } else if (ly === surface) {
          if (opts.mode === "flat") {
            id = surfaceBlockFor(surface, BIOME.GRASSLANDS, wx, wz);
          } else if (hydro === 2) {
            id = BLOCK.POND_BED;
          } else if (hydro === 1) {
            id = BLOCK.STREAM_BED;
          } else {
            id = surfaceBlockFor(surface, biome, wx, wz);
          }
        } else if (ly <= SEA_LEVEL && surface < SEA_LEVEL) {
          // Static sea fill — also covers stream/pond beds carved under SEA_LEVEL
          // so deep incision still reads as water in the channel, not a dry trench.
          id = BLOCK.WATER;
        }

        // Swamp: shallow puddles on low flats slightly above sea (static water, not hydro beds)
        if (
          opts.mode === "noise" &&
          biome === BIOME.SWAMP &&
          hydro === 0 &&
          id === BLOCK.AIR &&
          ly === surface + 1 &&
          surface <= SEA_LEVEL + 1 &&
          rand2(wx, wz, seed + 303) < 0.12
        ) {
          id = BLOCK.WATER;
        }

        if (id !== BLOCK.AIR) {
          chunk.blocks[localIndex(lx, ly, lz)] = id;
        }
      }
    }
  }

  // Pass 2: paths/roads (noise only) — after hydro; never on ponds/streams/sand.
  // Width from pathLaneAt (~50±10% arteries); paint PATH over biome on existing heights.
  if (opts.mode === "noise") {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = ox + lx;
        const wz = oz + lz;
        const ci = lz * CHUNK_SIZE + lx;
        const land = landY[ci]!;
        if (land < SEA_LEVEL) {
          laneCol[ci] = 0;
          continue;
        }
        const hydro = hydroCol[ci]! as HydroClass;
        // Ponds: no road footprint. Streams: keep bed; still record lane for
        // prop clear / rock bans so shoulders don't dump rocks into channels.
        if (hydro === 2) {
          laneCol[ci] = 0;
          continue;
        }
        const lane = pathLaneAt(wx, wz, seed);
        laneCol[ci] = lane;
        if (lane === 0) continue;
        if (hydro === 1) continue; // stream bed wins over tan PATH
        const s = surfY[ci]!;
        if (s < SEA_LEVEL) continue;
        // Beach/aqua sand — never tan overpaint (roads path around via pathLaneAt)
        if (chunk.get(lx, s, lz) === BLOCK.SAND) continue;
        placePathCell(chunk, lx, s, lz);
      }
    }
  }

  // Pass 2b: building/ruin SITE_PAD placeholders (playtest 2.12/2.16) — after paths,
  // paint on existing terrain surface only; never over ponds/streams/sea/paths;
  // oblong/kidney ~300 beside roads (clearance ring) w/ keepouts + origin ban.
  if (opts.mode === "noise") {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = ox + lx;
        const wz = oz + lz;
        const ci = lz * CHUNK_SIZE + lx;
        const land = landY[ci]!;
        if (land < SEA_LEVEL) continue;
        const hydro = hydroCol[ci]! as HydroClass;
        if (hydro === 1 || hydro === 2) continue;
        const lane = laneCol[ci]! as PathLane;
        // Never paint pads on any road/path footprint (core, footpath, shoulder)
        if (lane === 1 || lane === 2 || lane === 3) continue;
        if (!sitePadAt(wx, wz, seed)) continue;
        const s = surfY[ci]!;
        if (s < SEA_LEVEL) continue;
        const top = chunk.get(lx, s, lz);
        if (
          top === BLOCK.STREAM_BED ||
          top === BLOCK.POND_BED ||
          top === BLOCK.PATH
        ) {
          continue;
        }
        chunk.blocks[localIndex(lx, s, lz)] = BLOCK.SITE_PAD;
      }
    }
  }

  // Pass 2c: final PATH priority (playtest 2.16/2.17/2.19) — re-assert roads after
  // SITE_PAD so pink never overwrites dry road lanes. Streams stay STREAM_BED
  // (hydro===1); PATH never overpaints channels or aqua sand beaches.
  if (opts.mode === "noise") {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const ci = lz * CHUNK_SIZE + lx;
        const lane = laneCol[ci]! as PathLane;
        if (lane === 0) continue;
        const land = landY[ci]!;
        if (land < SEA_LEVEL) continue;
        const hydro = hydroCol[ci]! as HydroClass;
        if (hydro === 1 || hydro === 2) continue;
        const s = surfY[ci]!;
        if (s < SEA_LEVEL) continue;
        if (chunk.get(lx, s, lz) === BLOCK.SAND) continue;
        placePathCell(chunk, lx, s, lz);
      }
    }
  }

  // Pass 2d: BRIDGE decks at road×stream crossings (playtest 2.21).
  // pathLaneAt already stamps lane 2/3 on legal 45–90° bridge snaps inside the
  // channel; Pass 2 leaves STREAM_BED there. Here we raise a flat wood sheet at
  // the approach bank height so the travel way continues over the water.
  if (opts.mode === "noise") {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const ci = lz * CHUNK_SIZE + lx;
        const hydro = hydroCol[ci]! as HydroClass;
        if (hydro !== 1) continue;
        const lane = laneCol[ci]! as PathLane;
        if (lane === 0) continue; // only where road claims the channel
        const bedY = surfY[ci]!;
        // Approach deck: max of neighbouring dry road/land surfaces (match bank)
        let deckY = bedY + 3;
        let found = false;
        const nOff = [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
          [2, 0],
          [-2, 0],
          [0, 2],
          [0, -2],
          [3, 0],
          [-3, 0],
          [0, 3],
          [0, -3],
        ] as const;
        for (const [dx, dz] of nOff) {
          const nlx = lx + dx;
          const nlz = lz + dz;
          if (nlx < 0 || nlz < 0 || nlx >= CHUNK_SIZE || nlz >= CHUNK_SIZE) {
            continue;
          }
          const nci = nlz * CHUNK_SIZE + nlx;
          const nh = hydroCol[nci]! as HydroClass;
          if (nh === 1 || nh === 2) continue;
          const nLand = landY[nci]!;
          if (nLand < SEA_LEVEL) continue;
          const nSurf = surfY[nci]!;
          if (nSurf < SEA_LEVEL) continue;
          if (!found || nSurf > deckY) {
            deckY = nSurf;
            found = true;
          }
        }
        if (!found) {
          // Fall back to landform height before hydro cut (approach bank proxy)
          const land = landY[ci]!;
          deckY = Math.max(bedY + 3, Math.min(land, bedY + 12));
        }
        // Never drop deck below bed+2; clamp headroom
        placeBridgeCell(chunk, lx, lz, bedY, deckY);
      }
    }
  }

  // Pass 3: scatter props (noise only) — density cut hard for mobility
  if (opts.mode === "noise") {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = ox + lx;
        const wz = oz + lz;
        const ci = lz * CHUNK_SIZE + lx;
        const surface = surfY[ci]!;
        if (surface < SEA_LEVEL) continue;
        const dist = Math.hypot(wx, wz);
        const top = chunk.get(lx, surface, lz);
        const hydro = hydroCol[ci]! as HydroClass;

        // Streams: no grass, no rocks, no trees
        if (hydro === 1 || top === BLOCK.STREAM_BED) continue;
        // Bridge decks stay clear (playtest 2.21)
        if (top === BLOCK.BRIDGE) continue;

        const lane = laneCol[ci]! as PathLane;
        // Playtest 2.8: entire road/path footprint clear — core, footpath, shoulder
        if (lane === 1 || lane === 2 || lane === 3) continue;
        if (top === BLOCK.PATH) continue;
        if (top === BLOCK.SITE_PAD) continue;
        // Ponds: no rocks / trees / grass (playtest 2.8 ban)
        if (hydro === 2 || top === BLOCK.POND_BED) continue;
        if (!isPlantableSurface(top)) continue;
        if (chunk.get(lx, surface + 1, lz) !== BLOCK.AIR) continue;

        const biome = biomeCol[ci]! as BiomeId;
        const r = rand2(wx, wz, seed + 7);
        const r2 = rand2(wx, wz, seed + 19);

        // Edge margin: CHUNK_SIZE=16.
        // 0.2.53 edge>=3 opened almost the whole chunk (over-spawn). 0.2.54: edge>=5
        // (6×6 plantable core) + ~half rates so forests read sparse again.
        const edge = Math.min(lx, lz, CHUNK_SIZE - 1 - lx, CHUNK_SIZE - 1 - lz);

        // Big props: low density + clear radius near spawn (hub flatten)
        if (dist >= 28 && edge >= 5) {
          // 0.2.54: tree rates ~half of 0.2.53 (0.012/0.0035/0.006)
          if (biome === BIOME.FOREST && r < 0.006) {
            placePineTree(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
          if (biome === BIOME.GRASSLANDS && r < 0.0018) {
            placeOakTree(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
          if (biome === BIOME.SWAMP && r < 0.003) {
            placeWillowTree(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
          // Rock garden still denser than other biomes; −~1/3 vs 0.2.53
          if (biome === BIOME.ROCK_FIELDS && r < 0.012) {
            placeRock(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
          if (biome === BIOME.GRASSLANDS && r > 0.9996) {
            placeRock(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
          if (biome === BIOME.FOREST && r > 0.9997) {
            placeRock(chunk, lx, surface, lz, wx, wz, seed);
            continue;
          }
        }

        // Tall grass — full rescale: −20% density (gates ×0.8) + clump/short/bare
        // (bare + height mix inside placeTallGrass). Pre-rescale: 0.012/0.035/0.016/0.022.
        if (dist < 8) {
          if (r2 < 0.0096 && (biome === BIOME.GRASSLANDS || biome === BIOME.FOREST)) {
            placeTallGrass(chunk, lx, surface, lz, wx, wz, seed);
          }
        } else if (biome === BIOME.GRASSLANDS && r2 < 0.028) {
          placeTallGrass(chunk, lx, surface, lz, wx, wz, seed);
        } else if (biome === BIOME.FOREST && r2 < 0.0128) {
          placeTallGrass(chunk, lx, surface, lz, wx, wz, seed);
        } else if (biome === BIOME.SWAMP && r2 < 0.0176) {
          placeTallGrass(chunk, lx, surface, lz, wx, wz, seed);
        }
      }
    }
  }

  chunk.dirty = true;
  return chunk;
}

/** FNV-1a 32-bit over block bytes — stable golden hash helper. */
export function hashChunkBlocks(blocks: Uint8Array): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < blocks.length; i++) {
    h ^= blocks[i]!;
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/**
 * Parse shareable seed string → uint32.
 * Accepts decimal, 0x hex, or any string (FNV-ish mix).
 */
export function parseSeedString(raw: string): number {
  const s = raw.trim();
  if (!s) return 20260818;
  if (/^0x[0-9a-f]+$/i.test(s)) {
    const n = Number.parseInt(s.slice(2), 16);
    return Number.isFinite(n) ? n >>> 0 : 20260818;
  }
  if (/^-?\d+$/.test(s)) {
    const n = Number.parseInt(s, 10);
    return Number.isFinite(n) ? n >>> 0 : 20260818;
  }
  // String mix
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Canonical share form (decimal uint32). */
export function formatSeed(seed: number): string {
  return String(seed >>> 0);
}
